// Real API client for Symfony backend
import type { BookingSettings } from '../types/booking-types';
import type { Permission } from './permissions';
import { API_BASE_URL } from './api-base';
import { toast } from './toast';
import i18n from '../i18n';
import {
  ApiSession, ApiCollectionResponse, ApiGrade, ApiCurriculumItem, ApiCurriculumCategory, ApiRosterEntry, ApiStudentSummary, CoachRating,
  ApiAttendanceOverview, ApiSeasonReport, ApiPlanningSlot, ApiLocation, ApiMemberWeekSchedule, ApiSeason, ApiVacation, ApiGenerationReport, ApiPlanningStatus, ApiOccurrenceSummary, ApiOccurrenceDetail, ApiCalendarOccurrence, ApiClassDays,
  AttendanceStatus, ApiAttendanceStats, ApiDashboardGroup, ApiMemberHome, ApiMyAttendance, ApiTrainingStats, ApiTrainingEntries, ApiClassGroupRef, ApiClubActivity,
  ApiCoachClassDetail,
  AdminStudent, ClubFederation, LicenceStatus, MemberLicence, StudentCredentials, MemberInfoSheet, EmergencyContact, ApiMyCard, AdminImportSkip, ImportStudentRow, ImportStudentsResult, LicenceImportResult, AuditLogEntry, AuditLogEntityType, ApiProgram, ApiOfficialProgramSummary, ApiEnrollment, ApiBeltSettings, ApiAdminDashboard,
  RichContent, FlashcardSummary, FlashcardSession, FlashcardDeck, FlashcardMode, FlashcardDirection, FlashcardReviewRequest, FlashcardReviewResult, UsageArea, UsageScreen,
  ApiBadgeDefinition, ApiBadgeAward, ApiMyBadges, BadgeResultInput, BadgeKind, ApiBadgeCandidate, ApiBadgeAwardResult, ApiBadgeLibrary, BadgeRevokeReason,
  ApiEventSummary, ApiEvent, ApiEventSpeakerInput, ApiEventAudience, EventCategory, EventStatus, EventVisibility,
  EventRegistrationMode, EventFlyerFormat, EventAwardRule, ApiEventDayInput, ApiEventRegistration, EventRegistrationStatus,
  ApiEventAttendanceSheet, ApiEventAttendanceRow, ApiEventAwardBoard, ApiEventAwardResult,
  ApiClubSite,
  ApiSiteChange,
  ApiSiteLegal,
  ApiSitePhotoRemoval,
  ApiSiteMediaItem,
  ApiSiteMediaList,
  ApiStripeStatus,
} from "../types/api-types";
import type { ApiAdminSiteCoaches, ApiMySiteProfile } from "../types/site-coach-types";
import type { ApiSiteArticle, ApiSiteArticleInput, ApiSiteArticles } from "../types/site-article-types";
import type { ApiSiteGalleries, ApiSiteGallery, ApiSiteGalleryInput } from "../types/site-gallery-types";
import type { ApiSitePartnerInput, ApiSitePartners } from "../types/site-partner-types";

// "View as" mode: the backend reads these headers and treats the whole request
// as the target user. A super admin looking at a member is read-only; a parent
// acting as their child (userRef "#<id>") has the child's full rights. Kept per
// browser tab, so two tabs can act as two children.
const IMPERSONATION_KEY = 'impersonation';

export interface Impersonation {
  /** X-Switch-User value: a member's email (super admin) or "#<id>" (a parent's child). */
  userRef: string;
  clubId?: number | null;
}

/** One hit of the admin header search (GET /api/admin/search). */
export interface AdminSearchResult {
  id: number | null;
  name: string;
  email: string | null;
  /** member: a student of the club; staff: a coach, an admin or the owner. */
  type: "student" | "staff";
}

export function getImpersonation(): Impersonation | null {
  try {
    const raw = sessionStorage.getItem(IMPERSONATION_KEY);
    if (!raw) return null;
    const stored = JSON.parse(raw) as Partial<Impersonation> & { email?: string };
    // Sessions stored before "userRef" existed only had the email.
    const userRef = stored.userRef ?? stored.email;
    return userRef ? { userRef, clubId: stored.clubId ?? null } : null;
  } catch {
    return null;
  }
}

export function setImpersonation(value: Impersonation | null): void {
  // A different identity sees different data: nothing already read may be reused.
  invalidateApiCache();
  try {
    if (value) sessionStorage.setItem(IMPERSONATION_KEY, JSON.stringify(value));
    else sessionStorage.removeItem(IMPERSONATION_KEY);
  } catch {
    // storage unavailable: impersonation simply is not remembered
  }
}

/**
 * True while a super admin is looking at the app as somebody else (read-only),
 * as opposed to a parent acting as their own child (full rights).
 */
export function isReadOnlyImpersonation(): boolean {
  const impersonation = getImpersonation();

  return impersonation !== null && !impersonation.userRef.startsWith('#');
}

// Token refresh and logout are about the real session, never the impersonated one.
const NOT_IMPERSONATED = /\/(token\/refresh|logout)$/;

// These endpoints must not be retried through the silent-refresh path: the
// refresh call itself and logout are what a 401 retry would otherwise recurse into.
const NO_REFRESH_RETRY = /\/(token\/refresh|logout|auth\/otp\/verify)$/;

const apiFetch: typeof fetch = (input, init = {}) => {
  const impersonation = getImpersonation();
  if (!impersonation || NOT_IMPERSONATED.test(String(input))) {
    return fetch(input, init);
  }
  const headers = new Headers(init.headers);
  headers.set('X-Switch-User', impersonation.userRef);
  if (impersonation.clubId) headers.set('X-Club-Id', String(impersonation.clubId));
  return fetch(input, { ...init, headers });
};

/** Parts of the app a club can switch off from its settings. */
export type ClubSwitch = 'registration' | 'badges' | 'booking' | 'events' | 'website';

export type ClubRoleName = 'OWNER' | 'ADMIN' | 'COACH' | 'STUDENT' | 'ASSOCIATE';

export interface Membership {
  id: number;
  club: { id: number; name: string };
  role: ClubRoleName;
  /** Staff rank (OWNER / ADMIN / COACH), null for a plain member; `role` is the primary role. */
  staffRole?: ClubRoleName | null;
  /** Also trains in the club. */
  isMember?: boolean;
  status: 'PENDING' | 'ACTIVE' | 'REVOKED' | 'INACTIVE';
}

export interface ClubActivitySummary {
  key: string;
  name: string;
  federation: string | null;
  gradeTerm: 'belt' | 'level' | null;
  /** Features the activity gives and the club has not switched off. */
  features: ('grades' | 'program' | 'techniques')[];
}

export interface User {
  id: string;
  email: string;
  /** "First Last", for display. */
  name: string;
  firstName: string;
  lastName: string;
  /** Who the person is in the active club (ROLE_STUDENT for a member, ROLE_SUPER_ADMIN for the platform). Not what they may do: see `permissions`. */
  roles: string[];
  /** What the person may do in the active club (GET /api/me only); ask it with can() from lib/permissions. */
  permissions?: Permission[];
  /** Owner, admin or coach of the active club, whatever they were granted. */
  isStaff?: boolean;
  role?: ClubRoleName | null;
  staffRole?: ClubRoleName | null;
  isMember?: boolean;
  memberPaused?: boolean;
  isOwner?: boolean;
  isSuperAdmin?: boolean;
  /** Email of the super admin or parent behind a "view as" session. */
  impersonator?: string | null;
  /** Who is acting as this user: a super admin (read-only), a parent (full rights), or nobody. */
  actingAs?: 'admin' | 'guardian' | null;
  /** A super admin in a club through break-glass access: an admin there until it expires. */
  accessGrant?: { id: number; club: { id: number; name: string }; reason: string; expiresAt: string } | null;
  /** The real person's children across clubs (kept while acting as one of them, to switch). */
  children?: { id: number; name: string; firstName: string; club: { id: number; name: string; logoUrl?: string | null } | null; birthDate?: string | null; email?: string | null; signInAllowed?: boolean | null; canSignIn?: boolean; consentAge?: number; releaseRequested?: boolean }[];
  /** Children a club named this person the parent of, still to confirm (name and birth date). */
  pendingChildren?: number;
  /** The clubs behind those pending links (name and logo only), for the confirm screen. */
  pendingClubs?: { id: number; name: string; logoUrl?: string | null }[];
  /** Dev only: the details of the children still to confirm, so the confirm-child form can be autofilled. */
  pendingChildrenInfo?: { id: number; firstName: string; lastName: string; name: string; birthDate: string | null }[];
  /** Too many wrong answers while confirming: a club admin must reset it. */
  guardianLocked?: boolean;
  /** Children whose training history this person may read, even when no club links them any more. */
  historyChildren?: { id: number; name: string }[];
  memberships?: Membership[];
  locale?: string | null;
  club?: {
    id: string;
    name: string;
    /** A demonstration club: every screen in it says so. */
    isDemo?: boolean;
    /** What the club teaches, with the features it still uses; only on the signed-in user. */
    activities?: ClubActivitySummary[];
    /** Parts of the app the club turned on or off; only on the signed-in user. */
    features?: Partial<Record<ClubSwitch, boolean>>;
    /** At least one class takes bookings instead of a roster: members get the "Réservations" screen. */
    hasBookingClasses?: boolean;
    /** How the club words its bookable things: decided by the server. */
    bookingLabel?: 'outings' | 'bookings';
    /** The data processing agreement in force and whether the owner accepted it; only on the signed-in user. */
    dpa?: { version: string; accepted: boolean };
  } | null;
  classGroups?: { id: number; name: string; color: string | null }[];
  enrollments?: ApiEnrollment[];
}

/** The contact details a person, or a parent for a child, can correct themselves (Art. 16 GDPR). */
export interface PersonProfile {
  id: number;
  name: string;
  address: string | null;
  addressLine2: string | null;
  postalCode: string | null;
  city: string | null;
  phone: string | null;
  emergencyContacts: { name: string; relation: string; phone: string }[];
}

/** One person's optional consents in one club, as they can change them (Art. 7(3) GDPR). */
export interface ConsentEntry {
  person: { id: number; name: string; isMe: boolean };
  club: { id: number; name: string };
  consents: {
    purpose: string;
    /** An i18n key of the "registration" namespace for the builtin consents, the club's own words otherwise. */
    label: string;
    granted: boolean;
    recordedAt: string;
    givenBy: 'self' | 'guardian';
    /** False for the disability indicator: it is only declared on a registration form, here it can be withdrawn. */
    canGrant: boolean;
  }[];
}

/** A person's request to have their account deleted (Art. 17 GDPR), as they see it. */
export interface ErasureRequestState {
  /** Waiting for the emailed code, waiting for the platform's answer, or answered with a refusal. */
  status: 'awaiting_confirmation' | 'pending' | 'refused';
  createdAt: string;
  confirmedAt: string | null;
  /** When the platform has to answer by. */
  dueAt: string | null;
  handledAt: string | null;
  /** The reason, for a refusal. */
  note: string | null;
}

export interface LoginResponse {
  success: boolean;
  message: string;
  data: {
    token: string;
    user: User;
  };
}

export interface ApiError {
  error: {
    message: string;
    details?: unknown;
  };
}

/** A failed API call. Still an Error, so existing `err.message` handling keeps working. */
export class ApiHttpError extends Error {
  constructor(
    message: string,
    readonly status: number,
    /** The `error.details` object of the response, when it has one (e.g. `{ fields: { email: '…' } }`). */
    readonly details: Record<string, any> | null = null,
  ) {
    super(message);
    this.name = 'ApiHttpError';
  }

  /** Field-level messages of a 422 answer, by field name. */
  get fieldErrors(): Record<string, string> {
    return (this.details?.fields as Record<string, string> | undefined) ?? {};
  }
}

/** One kind of notification a person may receive (see NotificationType on the server). */
export type NotificationTypeKey = 'registration.submitted' | 'pdf_integration.requested';
export type NotificationFrequency = 'immediate' | 'daily';
/** Who a person is in the place a notification is about: what gives access to it. */
export type NotificationAudience = 'member' | 'coach' | 'admin' | 'super_admin';
export interface NotificationSettingItem {
  type: NotificationTypeKey;
  channel: 'email';
  /** The audience that gives access: the page labels the item with it. */
  audience: NotificationAudience;
  /** The effective state: true when the person receives this type. */
  enabled: boolean;
  /** The same value, under its former name. */
  subscribed: boolean;
  /** True for a type that is on until somebody turns it off. */
  default: boolean;
  frequency: NotificationFrequency;
  frequencies: NotificationFrequency[];
  /** A staff type: the club's staff screens may set it for a member of staff. */
  staff: boolean;
}
/** A person's notification settings in one place: one club, or the console. */
export interface NotificationSettings {
  /** Null for the console (platform) group of a super admin. */
  membershipId: number | null;
  scope: 'club' | 'platform';
  club: { id: number | null; name: string | null };
  items: NotificationSettingItem[];
}

export interface AdminUser {
  id: number;
  /** Null for members created without an email address. */
  email: string | null;
  /** "First Last", for display. */
  name: string;
  firstName: string;
  lastName: string;
  roles: string[];
  role: ClubRoleName | null;
  /** Staff rank, null for a plain member; `role` is the primary role. */
  staffRole: ClubRoleName | null;
  /** Also trains in the club. */
  isMember: boolean;
  /** The member side is paused (staff keep their access). */
  memberPaused: boolean;
  isOwner: boolean;
  /** What they may do in the club: the preset of their rank, plus what was granted, minus what was denied. */
  permissions?: Permission[];
  permissionPreset?: Permission[];
  permissionsGranted?: Permission[];
  permissionsDenied?: Permission[];
  status: 'PENDING' | 'ACTIVE' | 'REVOKED' | 'INACTIVE' | null;
  membershipId: number | null;
  /** When a login link was last emailed to them; null when none was sent yet. */
  invitedAt: string | null;
  createdByAdmin: boolean;
  gender?: string | null;
  birthDate?: string | null;
  /** This season's licence, the weakest of the person's licences. Only the admin lists carry it. */
  licence?: { season: string; status: LicenceStatus; number: string | null };
  /** One licence per federation the person is expected in this season. */
  licences?: MemberLicence[];
  /** A guardian's decision for a child: true/false, or null = by age. */
  signInAllowed?: boolean | null;
  /** Whether this account may sign in on its own right now. */
  canSignIn?: boolean;
  club: { id: number; name: string } | null;
  /** The person's own last use of the app (never their creation or an admin's edit). */
  lastActivityAt: string | null;
  /** The screen they were on then; null for accounts last seen before it existed. */
  lastActivityArea: UsageArea | null;
}

export interface AdminClub {
  id: number;
  name: string;
  description: string | null;
  address: string | null;
  phone: string | null;
  email: string | null;
  website: string | null;
  logoUrl: string | null;
  brandingColors: Record<string, string> | null;
  /** IANA timezone of the dojo; null = application default. */
  timezone: string | null;
  /** Language of the club's emails to people who have not chosen one; null = application default. */
  defaultLanguage: string | null;
  /** From name of this club's emails; null = the platform sender's name. */
  mailFromName?: string | null;
  /** From address of this club's emails; null = the platform sender's address. */
  mailFromAddress?: string | null;
  /** Age below which a child needs a guardian's permission to sign in alone. */
  digitalConsentAge?: number;
  /** What the club adds to the standard privacy information on its registration page; null = nothing. */
  privacyNotice?: string | null;
  /** When the owner last confirmed the team list is right (every six months is expected); null = never. */
  accessReviewedAt?: string | null;
  /** How long the club keeps what it collects, in months; never longer than the platform's default. */
  retention?: { abandonedMonths: number; filesMonths: number; registrationsMonths: number };
  /** When true, app:reports:send-monthly emails each student's monthly PDF to their guardians. */
  monthlyParentReportsEnabled?: boolean;
  features?: Partial<Record<ClubSwitch, boolean>>;
  /** The Keiko operator lets an AI assistant set this club up (read-only: only the operator switches it). */
  mcpEnabled?: boolean;
  userCount?: number;
  sessionCount?: number;
}

/**
 * The email sender of a club: what is stored, what the platform uses by default,
 * and the sender that would really be used.
 */
export interface ClubEmailSender {
  senderName: string | null;
  senderAddress: string | null;
  defaultName: string | null;
  defaultAddress: string;
  effective: { name: string | null; address: string };
}

/** The message to show for a failed call, whatever shape the error body has. */
/** A class's program comes as an IRI ("/api/programs/3"), an object, or not at all. */
function programIdOf(program: unknown): number | null {
  if (typeof program === 'string') return Number(program.split('/').pop()) || null;
  if (program && typeof program === 'object' && 'id' in program) return Number((program as { id: unknown }).id) || null;
  return null;
}

function errorMessage(body: Record<string, any> | null, status: number): string {
  // The user is on their way to /login; the JWT library's wording means nothing to them.
  if (status === 401) return i18n.t('common:errors.sessionExpired');
  const fromBody = body?.error?.message ?? body?.['hydra:description'] ?? body?.detail ?? body?.message;
  if (typeof fromBody === 'string' && fromBody !== '' && fromBody !== 'Access Denied.') {
    return fromBody;
  }
  if (status === 403) return i18n.t('common:errors.forbidden');
  return i18n.t('common:errors.unexpected');
}

// Every intentional write toasts "saved" (debounced, see toast.saved). Only sign-in plumbing,
// learning/quiz interactions and background uploads stay silent.
const SILENT_ENDPOINTS = /^\/(auth|token|logout|me$|superadmin)|\/(analysis|know-it)|^\/attendance|^\/admin\/imports\/\w+\/files|^\/admin\/registration-setup\/(quote|files)|^\/flashcards|^\/signalements/;

// --- Read cache ------------------------------------------------------------------
// A screen usually re-reads things it has already read (levels, grades, programs,
// the club's students), and several components on the same screen read the same
// endpoint independently. A short-lived GET cache plus in-flight de-duplication
// turns that into one network call and makes going back to a screen instant.
//
// Cached payloads are handed out by reference: callers treat API data as
// immutable and build new arrays/objects when they change something. Nothing is
// persisted, so a reload always reads the server.
const GET_TTL_MS = 30_000;
const GET_CACHE_MAX = 150;
/** Never cached: identity changes with "view as", and tokens are per-call. */
const UNCACHEABLE = /\/(me|memberships|token|logout|auth)\b/;

const getCache = new Map<string, { at: number; value: unknown }>();
const getInflight = new Map<string, Promise<unknown>>();
/** Bumped by every write: results that were already on the wire are then not stored. */
let cacheGeneration = 0;
/**
 * Who is signed in, as `"<id>:<accountLocale>"` (empty while signed out).
 * Grade and other server-localized names follow the account locale, so two
 * identities (or one identity after its language changed) must never share a
 * cached payload: mixed French/English names come from exactly that reuse.
 */
let cacheIdentity = '';

/**
 * Declares the signed-in identity the read cache belongs to. Changing it drops
 * every entry at once, so a payload read for one account/language is never
 * served for another.
 */
export function setCacheIdentity(ref: string): void {
  const next = ref ?? '';
  if (next === cacheIdentity) return;
  cacheIdentity = next;
  invalidateApiCache();
}

/** Who a cached answer belongs to: the UI language, the signed-in account, and whose eyes we are looking through. */
function cacheScope(): string {
  const impersonation = getImpersonation();
  return `${i18n.language}|${cacheIdentity}|${impersonation?.userRef ?? ''}|${impersonation?.clubId ?? ''}`;
}

/**
 * Drops every cached read. Called whenever the server may have moved on: any
 * write, a sign-in/sign-out, or a change of "view as".
 */
export function invalidateApiCache(): void {
  cacheGeneration += 1;
  getCache.clear();
  getInflight.clear();
}

class ApiClient {
  private baseUrl = API_BASE_URL;
  private token: string | null = null;

  constructor() {
    // Load token from localStorage on initialization
    this.token = localStorage.getItem('token');

    // Another tab refreshed the token or signed out: follow it.
    if (typeof window !== 'undefined') {
      window.addEventListener('storage', (event) => {
        if (event.key === 'token' || event.key === null) {
          this.token = localStorage.getItem('token');
        }
      });
    }
  }

  private async request<T>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<T> {
    const method = (options.method ?? 'GET').toUpperCase();

    if (method !== 'GET') {
      // Writes are never cached. authFetch drops every cached read as the write
      // goes out, so a screen refetching after a save sees fresh data.
      return this.send<T>(endpoint, options);
    }

    if (UNCACHEABLE.test(endpoint)) {
      return this.send<T>(endpoint, options);
    }

    return this.cachedRead<T>(endpoint, () => this.send<T>(endpoint, options));
  }

  /**
   * Remembers a read for GET_TTL_MS and lets callers asking for the same thing at
   * the same moment share one round-trip. `key` is the request path, query
   * included, so two different filters are two different entries.
   */
  private cachedRead<T>(key: string, load: () => Promise<T>): Promise<T> {
    // The generation is part of the key, so a caller arriving after a write never
    // joins (or reuses) an answer that predates it.
    const scoped = `${cacheGeneration}|${cacheScope()}|${key}`;

    const hit = getCache.get(scoped);
    if (hit && Date.now() - hit.at < GET_TTL_MS) {
      return Promise.resolve(hit.value as T);
    }

    // Two components reading the same list at the same moment share one request.
    const pending = getInflight.get(scoped);
    if (pending) {
      return pending as Promise<T>;
    }

    const generation = cacheGeneration;
    const request = load()
      .then((value) => {
        // A write landed while this was on the wire: the caller still gets the
        // answer it asked for, but it is not remembered as current.
        if (generation === cacheGeneration) {
          if (getCache.size >= GET_CACHE_MAX) {
            const oldest = getCache.keys().next().value;
            if (oldest !== undefined) getCache.delete(oldest);
          }
          getCache.set(scoped, { at: Date.now(), value });
        }
        return value;
      })
      .finally(() => {
        getInflight.delete(scoped);
      });

    getInflight.set(scoped, request);
    return request;
  }

  /** One authenticated round-trip: headers, "view as", error shaping and the "saved" toast. */
  private async send<T>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<T> {
    const url = `${this.baseUrl}${endpoint}`;

    const headers: Record<string, string> = {
      // A FormData body must keep the multipart boundary the browser sets,
      // so it is the one case where JSON is not the content type.
      ...(options.body instanceof FormData ? {} : { 'Content-Type': 'application/json' }),
      'Accept-Language': i18n.language,
      ...(options.headers as Record<string, string> | undefined),
    };

    let response: Response;
    try {
      response = await this.authFetch(url, { ...options, headers });
    } catch {
      throw new ApiHttpError(i18n.t('common:errors.network'), 0);
    }

    if (!response.ok) {
      const errorData: Partial<ApiError> & Record<string, any> | null = await response.json().catch(() => null);
      const details = errorData?.error?.details;
      throw new ApiHttpError(
        errorMessage(errorData, response.status),
        response.status,
        details && typeof details === 'object' && !Array.isArray(details) ? (details as Record<string, any>) : null,
      );
    }

    const method = (options.method ?? 'GET').toUpperCase();
    if (method !== 'GET' && !SILENT_ENDPOINTS.test(endpoint)) {
      toast.saved(i18n.t("common:saved"));
    }

    return response.json();
  }

  /** Typed JSON call for feature modules (see superadmin-api.ts): same auth, refresh and "view as" handling as every other call. */
  call<T>(endpoint: string, options: RequestInit = {}): Promise<T> {
    return this.request<T>(endpoint, options);
  }

  // Session expired or invalid: clear stored credentials and send the user back to login.
  private handleUnauthorized(): void {
    invalidateApiCache();
    this.token = null;
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    if (typeof window !== 'undefined' && window.location.pathname !== '/login') {
      window.location.href = '/login';
    }
  }

  // Authentication methods
  async requestMagicLink(email: string): Promise<void> {
    const response = await this.request<{ success: boolean; message: string }>('/auth/magic-link/request', {
      method: 'POST',
      body: JSON.stringify({ email }),
    });

    if (!response.success) {
      throw new Error(response.message || 'Failed to send login link');
    }
  }

  async verifyMagicLink(token: string): Promise<User> {
    const response: LoginResponse = await this.request('/auth/magic-link/verify', {
      method: 'POST',
      body: JSON.stringify({ token }),
    });

    return this.storeSession(response, 'This login link is invalid or has expired');
  }

  /** Signs in with the 6-digit code from the email: for an installed PWA, where the link opens elsewhere. */
  async verifyOtp(email: string, code: string): Promise<User> {
    const response: LoginResponse = await this.request('/auth/otp/verify', {
      method: 'POST',
      body: JSON.stringify({ email, code }),
    });

    return this.storeSession(response, 'This code is invalid or has expired');
  }

  private storeSession(response: LoginResponse, failure: string): User {
    if (response.success && response.data.token) {
      this.token = response.data.token;
      localStorage.setItem('token', this.token);
      localStorage.setItem('user', JSON.stringify(response.data.user));
      return response.data.user;
    }

    throw new Error(response.message || failure);
  }

  // Dev-only: skips email delivery, disabled server-side outside the dev environment.
  async devLogin(email: string): Promise<User> {
    const response: LoginResponse = await this.request('/auth/dev-login', {
      method: 'POST',
      body: JSON.stringify({ email }),
    });

    if (response.success && response.data.token) {
      this.token = response.data.token;
      localStorage.setItem('token', this.token);
      localStorage.setItem('user', JSON.stringify(response.data.user));
      return response.data.user;
    }

    throw new Error(response.message || 'Login failed');
  }

  async getCurrentUser(): Promise<User | null> {
    try {
      const user = localStorage.getItem('user');
      if (user) {
        return JSON.parse(user);
      }
      return null;
    } catch {
      return null;
    }
  }

  // Saves the language on the account and keeps the cached user in sync.
  async updateLocale(locale: string): Promise<User> {
    const response: { data: User } = await this.request('/me', {
      method: 'PATCH',
      body: JSON.stringify({ locale }),
    });
    localStorage.setItem('user', JSON.stringify(response.data));
    return response.data;
  }

  async logout(): Promise<void> {
    try {
      await apiFetch(`${this.baseUrl}/logout`, { method: 'POST', credentials: 'include' });
    } catch {
      // best-effort server-side revoke; local state is cleared regardless
    }
    this.token = null;
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    setImpersonation(null);
    setCacheIdentity('');
    // Another account may sign in next: nothing read as this one may be reused.
    invalidateApiCache();
  }

  /**
   * fetch() with the access token, the refresh cookie and "view as" headers.
   * A 401 first tries the newer token another call may already have obtained,
   * then one refresh; only if that fails is the session over and the user
   * sent to /login. Every authenticated call must go through here, or an
   * expired access token logs the user out without a refresh attempt.
   */
  private async authFetch(url: string, init: RequestInit = {}): Promise<Response> {
    const perform = (): Promise<Response> => {
      const headers = new Headers(init.headers);
      // Belt/grade names (and other server-localized payloads) come back in the
      // request locale. Some loaders call authFetch directly instead of send(),
      // so the header is set here too — a caller may still override it.
      if (!headers.has('Accept-Language')) headers.set('Accept-Language', i18n.language);
      if (this.token) {
        headers.set('Authorization', `Bearer ${this.token}`);
      } else {
        headers.delete('Authorization');
      }
      return apiFetch(url, { ...init, headers, credentials: 'include' });
    };

    const sentWith = this.token;
    let response = await perform();

    // A 401 usually means the short-lived access token (15 min) expired, or a
    // deploy rotated the JWT keys — not that the session is over.
    if (response.status === 401 && sentWith && !NO_REFRESH_RETRY.test(url)) {
      const renewed = this.token !== sentWith || await this.refreshAccessToken(sentWith);
      if (renewed && this.token) {
        response = await perform();
      }
    }

    if (response.status === 401 && !NO_REFRESH_RETRY.test(url)) {
      this.handleUnauthorized();
    }

    // Any write may have changed what the read cache is holding. Done here rather
    // than in each caller so a new write endpoint cannot forget it. Plus a few
    // writers (createSession, the class-level editors) bypass request() entirely.
    if ((init.method ?? 'GET').toUpperCase() !== 'GET') {
      invalidateApiCache();
    }

    return response;
  }

  /**
   * Exchanges the httpOnly refresh_token cookie for a new access token.
   *
   * The refresh token is single-use: each exchange revokes the cookie it was
   * given. Two exchanges racing with the same cookie (parallel 401s, the
   * startup check plus the timer, two tabs) make one of them fail and can
   * leave the browser holding a revoked cookie, which ends the session at the
   * next expiry. So exchanges are shared inside a tab and serialized across
   * tabs, and a tab that finds a token newer than the one it wanted to replace
   * adopts it instead of spending the cookie again.
   *
   * @param stale the access token the caller wants replaced (default: the current one)
   */
  refreshAccessToken(stale: string | null = this.token): Promise<boolean> {
    this.refreshing ??= this.withRefreshLock(() => this.exchangeRefreshCookie(stale))
      .finally(() => { this.refreshing = null; });

    return this.refreshing;
  }

  private refreshing: Promise<boolean> | null = null;

  private withRefreshLock(run: () => Promise<boolean>): Promise<boolean> {
    if (typeof navigator !== 'undefined' && navigator.locks) {
      // The lock holds until run() settles; then() unwraps its promise for the types.
      return navigator.locks.request('judo-token-refresh', run).then((granted) => granted);
    }
    return run();
  }

  private async exchangeRefreshCookie(stale: string | null): Promise<boolean> {
    const stored = localStorage.getItem('token');
    if (stored && stored !== stale) {
      this.token = stored;
      return true;
    }

    try {
      const response = await apiFetch(`${this.baseUrl}/token/refresh`, {
        method: 'POST',
        credentials: 'include',
      });

      if (!response.ok) {
        return false;
      }

      const data = await response.json();
      const newToken: string | undefined = data.token;
      if (!newToken) {
        return false;
      }

      this.token = newToken;
      localStorage.setItem('token', newToken);
      return true;
    } catch {
      return false;
    }
  }

  // Check if user is authenticated
  isAuthenticated(): boolean {
    return !!this.token;
  }

  // Get current token
  getToken(): string | null {
    return this.token;
  }

  /**
   * Fire-and-forget "I am here" ping (see lib/usage.ts). Deliberately not
   * routed through request(): it must never toast, never retry, and must
   * survive the tab going away — a lost ping costs nothing, a blocking one
   * would cost a navigation.
   */
  async sendUsage(screen: UsageScreen): Promise<void> {
    if (!this.token) return;
    // A super admin looking at the app as a member is not that member using it:
    // the server refuses the ping outright during a read-only switch, and not
    // sending it saves a pointless 403 on every screen they visit.
    if (isReadOnlyImpersonation()) return;

    try {
      await apiFetch(`${this.baseUrl}/usage`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept-Language': i18n.language,
          Authorization: `Bearer ${this.token}`,
        },
        body: JSON.stringify({ screen }),
        credentials: 'include',
        keepalive: true,
      });
    } catch {
      // Offline, or the tab closed mid-flight: the next ping says the same thing.
    }
  }

  // Technique management
  async getTechniques(): Promise<any[]> {
    return this.cachedRead('/techniques', () => this.loadTechniques());
  }

  private async loadTechniques(): Promise<any[]> {
    try {
      const response = await this.authFetch(`${this.baseUrl}/techniques`, {
        method: 'GET',
        headers: {
          'Accept': 'application/ld+json',
          ...(this.token ? { 'Authorization': `Bearer ${this.token}` } : {})
        }
      });

      if (response.status === 401) {
        this.handleUnauthorized();
      }

      if (!response.ok) {
        throw new Error(`Failed to fetch techniques: ${response.status}`);
      }
      
      const data = await response.json();
      console.log('API Techniques response:', data);
      
      // Handle API Platform response format
      if (data && data.member && Array.isArray(data.member)) {
        return data.member;
      } else if (data && data['hydra:member'] && Array.isArray(data['hydra:member'])) {
        return data['hydra:member'];
      } else if (data && Array.isArray(data)) {
        return data;
      } else {
        console.warn('Unexpected API response format for techniques:', data);
        return [];
      }
    } catch (error) {
      console.error('API Client - Get techniques error:', error);
      throw error;
    }
  }

  // Session management
  async getSessions(): Promise<ApiSession[]> {
    return this.cachedRead('/sessions', () => this.loadSessions());
  }

  private async loadSessions(): Promise<ApiSession[]> {
    try {
      const response = await this.authFetch(`${this.baseUrl}/sessions`, {
        method: 'GET',
        headers: {
          'Accept': 'application/ld+json',
          ...(this.token ? { 'Authorization': `Bearer ${this.token}` } : {})
        }
      });

      if (response.status === 401) {
        this.handleUnauthorized();
      }

      if (!response.ok) {
        throw new Error(`Failed to fetch sessions: ${response.status}`);
      }
      
      const data = await response.json();
      console.log('API Sessions response:', data);
      
      // Handle API Platform response format
      if (data && data.member && Array.isArray(data.member)) {
        return data.member;
      } else if (data && data['hydra:member'] && Array.isArray(data['hydra:member'])) {
        return data['hydra:member'];
      } else if (data && Array.isArray(data)) {
        return data;
      } else {
        console.warn('Unexpected API response format for sessions:', data);
        return [];
      }
    } catch (error) {
      console.error('API Client - Get sessions error:', error);
      throw error;
    }
  }

  async createSession(
    title: string,
    scheduledDate: string,
    techniqueIds: string[],
    classGroupId?: string,
    notes?: string
  ) {
    try {
      console.log('API Client - Creating session with token:', this.token);
      
      // Using API Platform endpoint
      const payload = {
        title,
        scheduledDate,
        // Convert notes from string to array as required by the backend
        notes: notes ? [notes] : [],
        durationMinutes: 60, // Default duration
        status: 'active'
      };
      
      // Add class level if provided
      if (classGroupId) {
        payload['classGroup'] = `/api/class_groups/${classGroupId}`;
      }
      
      console.log('API Client - Request payload:', payload);
      
      // First create the session
      const response = await this.authFetch(`${this.baseUrl}/sessions`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          ...(this.token ? { 'Authorization': `Bearer ${this.token}` } : {})
        },
        body: JSON.stringify(payload)
      });
      
      const responseData = await response.text();

      if (response.status === 401) {
        this.handleUnauthorized();
      }

      if (!response.ok) {
        let errorMessage;
        try {
          const errorData = JSON.parse(responseData);
          errorMessage = errorData.error?.message || `Server error: ${response.status}`;
        } catch (e) {
          errorMessage = `Server error: ${response.status} - ${responseData.substring(0, 100)}`;
        }
        throw new Error(errorMessage);
      }
      
      // Parse the response to get the session ID
      const sessionData = JSON.parse(responseData);
      
      // Now add techniques if provided
      if (techniqueIds && techniqueIds.length > 0) {
        // For each technique, add it to the session
        for (const techniqueId of techniqueIds) {
          try {
            await this.authFetch(`${this.baseUrl}/sessions/${sessionData.id}/techniques`, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                'Accept': 'application/json',
                ...(this.token ? { 'Authorization': `Bearer ${this.token}` } : {})
              },
              body: JSON.stringify({
                technique: `/api/techniques/${techniqueId}`
              })
            });
          } catch (error) {
            console.error(`Failed to add technique ${techniqueId} to session:`, error);
          }
        }
      }
      
      // Return the session data
      return sessionData;
    } catch (error) {
      console.error('API Client - Session creation error:', error);
      throw error;
    }
  }

  // Curriculum
  async getGrades(): Promise<ApiGrade[]> {
    return this.cachedRead('/grades', () => this.loadGrades());
  }

  private async loadGrades(): Promise<ApiGrade[]> {
    try {
      const response = await this.authFetch(`${this.baseUrl}/grades`, {
        method: 'GET',
        headers: {
          'Accept': 'application/ld+json',
          ...(this.token ? { 'Authorization': `Bearer ${this.token}` } : {})
        }
      });
      if (response.status === 401) {
        this.handleUnauthorized();
      }
      if (!response.ok) {
        throw new Error(`Failed to fetch grades: ${response.status}`);
      }
      const data = await response.json();
      if (data && data.member && Array.isArray(data.member)) {
        return data.member;
      } else if (data && data['hydra:member'] && Array.isArray(data['hydra:member'])) {
        return data['hydra:member'];
      } else if (Array.isArray(data)) {
        return data;
      }
      return [];
    } catch (error) {
      console.error('API Client - Get grades error:', error);
      throw error;
    }
  }

  // Curriculum (student)
  async getMyCurriculum(programId?: number, gradeId?: number): Promise<{ grade: ApiGrade | null; items: ApiCurriculumItem[]; categories: ApiCurriculumCategory[]; enrollments: ApiEnrollment[] }> {
    const params = new URLSearchParams();
    if (programId) params.set('programId', String(programId));
    if (gradeId) params.set('gradeId', String(gradeId));
    const query = params.toString() ? `?${params.toString()}` : '';
    const response = await this.request<{ success: boolean; data: { grade: ApiGrade | null; items: ApiCurriculumItem[]; categories: ApiCurriculumCategory[]; enrollments: ApiEnrollment[] } }>(`/curriculum/my${query}`);
    return response.data;
  }

  async toggleKnowIt(itemId: number): Promise<{ itemId: number; studentKnowsIt: boolean }> {
    const response = await this.request<{ success: boolean; data: { itemId: number; studentKnowsIt: boolean } }>(
      `/curriculum/items/${itemId}/know-it`,
      { method: 'POST' }
    );
    return response.data;
  }

  // Flashcards (student)
  async getFlashcardSummary(directions: FlashcardDirection[]): Promise<FlashcardSummary> {
    const response = await this.request<{ data: FlashcardSummary }>(`/flashcards/summary?directions=${directions.join(',')}`);
    return response.data;
  }

  async getFlashcardSession(deck: FlashcardDeck, mode: FlashcardMode, directions: FlashcardDirection[], limit = 20): Promise<FlashcardSession> {
    const response = await this.request<{ data: FlashcardSession }>(`/flashcards/session?deck=${deck}&mode=${mode}&directions=${directions.join(',')}&limit=${limit}`);
    return response.data;
  }

  async reviewFlashcard(body: FlashcardReviewRequest): Promise<FlashcardReviewResult> {
    const response = await this.request<{ data: FlashcardReviewResult }>('/flashcards/review', {
      method: 'POST',
      body: JSON.stringify(body),
    });
    return response.data;
  }

  async undoFlashcardReview(logId: number): Promise<void> {
    await this.request(`/flashcards/review/${logId}`, { method: 'DELETE' });
  }

  // Curriculum (coach)
  async getRoster(): Promise<ApiRosterEntry[]> {
    const response = await this.request<{ success: boolean; data: { students: ApiRosterEntry[] } }>('/curriculum/roster');
    return response.data.students;
  }

  async getStudentCurriculum(studentId: number, programId?: number, gradeId?: number): Promise<{ student: ApiStudentSummary; grade: ApiGrade | null; items: ApiCurriculumItem[]; enrollments: ApiEnrollment[] }> {
    const params = new URLSearchParams();
    if (programId) params.set('programId', String(programId));
    if (gradeId) params.set('gradeId', String(gradeId));
    const query = params.toString() ? `?${params.toString()}` : '';
    const response = await this.request<{ success: boolean; data: { student: ApiStudentSummary; grade: ApiGrade | null; items: ApiCurriculumItem[]; enrollments: ApiEnrollment[] } }>(
      `/curriculum/students/${studentId}${query}`
    );
    return response.data;
  }

  async rateItem(studentId: number, itemId: number, rating: CoachRating): Promise<{ itemId: number; coachRating: CoachRating }> {
    const response = await this.request<{ success: boolean; data: { itemId: number; coachRating: CoachRating } }>(
      `/curriculum/students/${studentId}/items/${itemId}/rate`,
      { method: 'POST', body: JSON.stringify({ rating }) }
    );
    return response.data;
  }

  async getPromotePreview(studentId: number, gradeId: number, programId?: number): Promise<{ remaining: number; total: number }> {
    const response = await this.request<{ success: boolean; data: { remaining: number; total: number } }>(
      `/curriculum/students/${studentId}/promote-preview?gradeId=${gradeId}${programId ? `&programId=${programId}` : ''}`
    );
    return response.data;
  }

  /** expectedFromGradeId: the grade the caller saw (null: none yet). Another club moving it first gives a 409 carrying details.currentGrade. */
  /** `correction`: fixes a mistake (any grade, lower included); a plain promotion never lowers the grade. */
  async promoteStudent(studentId: number, gradeId: number, programId?: number, validateRemaining = false, expectedFromGradeId?: number | null, correction = false): Promise<{ studentId: number; grade: ApiGrade; validatedItems: number }> {
    const response = await this.request<{ success: boolean; data: { studentId: number; grade: ApiGrade; validatedItems: number } }>(
      `/curriculum/students/${studentId}/promote`,
      { method: 'POST', body: JSON.stringify({ gradeId, programId, validateRemaining, ...(expectedFromGradeId !== undefined ? { expectedFromGradeId } : {}), ...(correction ? { correction } : {}) }) }
    );
    return response.data;
  }

  // --- Week schedule (every member) -----------------------------------------

  async getWeekSchedule(): Promise<ApiMemberWeekSchedule> {
    const response = await this.request<{ success: boolean; data: ApiMemberWeekSchedule }>('/schedule');
    return response.data;
  }

  // --- Planning (coach) ---------------------------------------------------

  async getPlanningSlots(): Promise<ApiPlanningSlot[]> {
    const response = await this.request<{ success: boolean; data: { slots: ApiPlanningSlot[] } }>('/planning/slots');
    return response.data.slots;
  }

  async getLocations(): Promise<ApiLocation[]> {
    const response = await this.request<{ success: boolean; data: { locations: ApiLocation[] } }>('/planning/locations');
    return response.data.locations;
  }

  async createLocation(input: { name: string; address?: string | null }): Promise<ApiLocation> {
    const response = await this.request<{ success: boolean; data: { location: ApiLocation } }>('/planning/locations', {
      method: 'POST',
      body: JSON.stringify(input),
    });
    return response.data.location;
  }

  async updateLocation(id: number, input: Partial<{ name: string; address: string | null; latitude: number | null; longitude: number | null }>): Promise<ApiLocation> {
    const response = await this.request<{ success: boolean; data: { location: ApiLocation } }>(`/planning/locations/${id}`, {
      method: 'PUT',
      body: JSON.stringify(input),
    });
    return response.data.location;
  }

  async deleteLocation(id: number): Promise<void> {
    await this.request(`/planning/locations/${id}`, { method: 'DELETE' });
  }

  /** Rooms answer with the whole location so the screen can swap it in. */
  async createRoom(locationId: number, name: string): Promise<ApiLocation> {
    const response = await this.request<{ success: boolean; data: { location: ApiLocation } }>(`/planning/locations/${locationId}/rooms`, {
      method: 'POST',
      body: JSON.stringify({ name }),
    });
    return response.data.location;
  }

  async updateRoom(id: number, input: Partial<{ name: string; position: number }>): Promise<ApiLocation> {
    const response = await this.request<{ success: boolean; data: { location: ApiLocation } }>(`/planning/rooms/${id}`, {
      method: 'PUT',
      body: JSON.stringify(input),
    });
    return response.data.location;
  }

  async deleteRoom(id: number): Promise<ApiLocation> {
    const response = await this.request<{ success: boolean; data: { location: ApiLocation } }>(`/planning/rooms/${id}`, { method: 'DELETE' });
    return response.data.location;
  }

  async createPlanningSlot(input: { classGroupId: number; locationId?: number | null; roomId?: number | null; weekday: number; startTime: string; endTime: string; weeks?: number[] | null; outsideHolidays?: boolean; tags?: string[] }): Promise<ApiPlanningSlot> {
    const response = await this.request<{ success: boolean; data: { slot: ApiPlanningSlot } }>('/planning/slots', {
      method: 'POST',
      body: JSON.stringify(input),
    });
    return response.data.slot;
  }

  async updatePlanningSlot(id: number, input: Partial<{ classGroupId: number; locationId: number | null; roomId: number | null; weekday: number; startTime: string; endTime: string; weeks: number[] | null; outsideHolidays: boolean; tags: string[] }>): Promise<ApiPlanningSlot> {
    const response = await this.request<{ success: boolean; data: { slot: ApiPlanningSlot } }>(`/planning/slots/${id}`, {
      method: 'PUT',
      body: JSON.stringify(input),
    });
    return response.data.slot;
  }

  async deletePlanningSlot(id: number): Promise<void> {
    await this.request(`/planning/slots/${id}`, { method: 'DELETE' });
  }

  async getSeasons(): Promise<ApiSeason[]> {
    const response = await this.request<{ success: boolean; data: { seasons: ApiSeason[] } }>('/planning/seasons');
    return response.data.seasons;
  }

  async createSeason(input: { label: string; startDate: string; endDate: string; isCurrent?: boolean }): Promise<ApiSeason> {
    const response = await this.request<{ success: boolean; data: { season: ApiSeason } }>('/planning/seasons', {
      method: 'POST',
      body: JSON.stringify(input),
    });
    return response.data.season;
  }

  async updateSeason(id: number, input: Partial<{ label: string; startDate: string; endDate: string; isCurrent: boolean }>): Promise<ApiSeason> {
    const response = await this.request<{ success: boolean; data: { season: ApiSeason } }>(`/planning/seasons/${id}`, {
      method: 'PUT',
      body: JSON.stringify(input),
    });
    return response.data.season;
  }

  async deleteSeason(id: number): Promise<void> {
    await this.request(`/planning/seasons/${id}`, { method: 'DELETE' });
  }

  async getVacations(): Promise<ApiVacation[]> {
    const response = await this.request<{ success: boolean; data: { vacations: ApiVacation[] } }>('/planning/vacations');
    return response.data.vacations;
  }

  async createVacation(input: { label: string; startDate: string; endDate: string }): Promise<ApiVacation> {
    const response = await this.request<{ success: boolean; data: { vacation: ApiVacation } }>('/planning/vacations', {
      method: 'POST',
      body: JSON.stringify(input),
    });
    return response.data.vacation;
  }

  async deleteVacation(id: number): Promise<void> {
    await this.request(`/planning/vacations/${id}`, { method: 'DELETE' });
  }

  async importMartiniqueVacations(): Promise<{ imported: number; skippedPast: number }> {
    const response = await this.request<{ success: boolean; message: string; data: { imported: number; skippedPast: number } }>(
      '/planning/vacations/import-martinique',
      { method: 'POST' }
    );
    return response.data;
  }

  async generatePlanning(seasonId?: number, dryRun = false): Promise<ApiGenerationReport> {
    const response = await this.request<{ success: boolean; data: ApiGenerationReport }>('/planning/generate', {
      method: 'POST',
      body: JSON.stringify({ ...(seasonId ? { seasonId } : {}), ...(dryRun ? { dryRun } : {}) }),
    });
    return response.data;
  }

  /** True once slots, holidays or seasons changed since the classes were last generated. */
  async getPlanningStatus(): Promise<ApiPlanningStatus> {
    const response = await this.request<{ success: boolean; data: ApiPlanningStatus }>('/planning/status');
    return response.data;
  }

  /** Classes planned between two days (inclusive), for the admin calendar. */
  async getOccurrences(from: string, to: string, classGroupId?: number): Promise<ApiCalendarOccurrence[]> {
    const params = new URLSearchParams({ from, to });
    if (classGroupId) params.set('classGroupId', String(classGroupId));
    const response = await this.request<{ success: boolean; data: { occurrences: ApiCalendarOccurrence[] } }>(`/planning/occurrences?${params.toString()}`);
    return response.data.occurrences;
  }

  async createOccurrence(input: { classGroupId: number; locationId?: number | null; roomId?: number | null; date: string; startTime: string; endTime: string; tags?: string[] }): Promise<ApiOccurrenceSummary> {
    const response = await this.request<{ success: boolean; data: { occurrence: ApiOccurrenceSummary } }>('/planning/occurrences', {
      method: 'POST',
      body: JSON.stringify(input),
    });
    return response.data.occurrence;
  }

  async updateOccurrence(id: number, input: { classGroupId?: number; locationId?: number | null; roomId?: number | null; date?: string; startTime?: string; endTime?: string; tags?: string[] }): Promise<ApiOccurrenceSummary> {
    const response = await this.request<{ success: boolean; data: { occurrence: ApiOccurrenceSummary } }>(`/planning/occurrences/${id}`, {
      method: 'PUT',
      body: JSON.stringify(input),
    });
    return response.data.occurrence;
  }

  async deleteOccurrence(id: number): Promise<void> {
    await this.request(`/planning/occurrences/${id}`, { method: 'DELETE' });
  }

  /** Calls a class off; members see it struck through, with the reason. */
  async cancelOccurrence(id: number, reason: string): Promise<void> {
    await this.request(`/planning/occurrences/${id}/cancel`, { method: 'POST', body: JSON.stringify({ reason }) });
  }

  async restoreOccurrence(id: number): Promise<void> {
    await this.request(`/planning/occurrences/${id}/cancel`, { method: 'DELETE' });
  }

  /** The member's calendar feed address, made on first use. */
  async createCalendarLink(): Promise<string> {
    const response = await this.request<{ success: boolean; data: { url: string } }>('/me/calendar-link', { method: 'POST' });
    return response.data.url;
  }

  async deleteCalendarLink(): Promise<void> {
    await this.request('/me/calendar-link', { method: 'DELETE' });
  }

  async downloadOccurrencesIcs(classGroupId?: number): Promise<void> {
    const query = classGroupId ? `?classGroupId=${classGroupId}` : '';
    const response = await this.authFetch(`${this.baseUrl}/planning/occurrences.ics${query}`, {
      headers: this.token ? { Authorization: `Bearer ${this.token}` } : {},
    });

    if (response.status === 401) {
      this.handleUnauthorized();
      return;
    }
    if (!response.ok) {
      throw new Error(i18n.t("common:errors.calendarDownload"));
    }

    const blob = await response.blob();
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'planning.ics';
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
  }

  // --- Reports -----------------------------------------------------------

  async getSeasonReport(): Promise<ApiSeasonReport> {
    const response = await this.request<{ success: boolean; data: ApiSeasonReport }>('/reports/season');
    return response.data;
  }

  /** Downloads a report PDF the browser generated server-side (Gotenberg); the endpoint is one of the /reports/*.pdf routes. */
  async downloadReportPdf(endpoint: string, filename: string): Promise<void> {
    const response = await this.authFetch(`${this.baseUrl}${endpoint}`, {
      headers: this.token ? { Authorization: `Bearer ${this.token}` } : {},
    });

    if (response.status === 401) {
      this.handleUnauthorized();
      return;
    }
    if (!response.ok) {
      throw new Error(i18n.t('common:errors.reportDownload'));
    }

    const blob = await response.blob();
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
  }

  /** The contact details of the signed-in person, or of a child they are the guardian of. */
  async getPersonProfile(personId: number): Promise<PersonProfile> {
    return (await this.request<{ data: PersonProfile }>(`/me/people/${personId}/profile`)).data;
  }

  /** Replaces those details; a field left out is cleared. */
  async savePersonProfile(personId: number, input: Omit<PersonProfile, 'id' | 'name'>): Promise<PersonProfile> {
    return (await this.request<{ data: PersonProfile }>(`/me/people/${personId}/profile`, { method: 'PUT', body: JSON.stringify(input) })).data;
  }

  /** The signed-in person's optional consents in each club, then their children's. */
  async getMyConsents(): Promise<ConsentEntry[]> {
    return (await this.request<{ data: ConsentEntry[] }>('/me/consents')).data;
  }

  /** Gives or takes back one consent, for oneself or a child. */
  async setMyConsent(personId: number, clubId: number, purpose: string, granted: boolean): Promise<void> {
    await this.request('/me/consents', { method: 'PUT', body: JSON.stringify({ personId, clubId, purpose, granted }) });
  }

  /** The person's own request to have their account deleted: open, or the last one that was refused; null when there is none. */
  async getMyErasureRequest(): Promise<ErasureRequestState | null> {
    return (await this.request<{ data: ErasureRequestState | null }>('/me/erasure-request')).data;
  }

  /** Starts a request, or sends a new code for one not confirmed yet. The code goes to the person's email. */
  async requestErasure(): Promise<ErasureRequestState> {
    return (await this.request<{ data: ErasureRequestState }>('/me/erasure-request', { method: 'POST' })).data;
  }

  /** Types the code from the email: the request then waits for the platform's answer. */
  async confirmErasure(code: string): Promise<ErasureRequestState> {
    return (await this.request<{ data: ErasureRequestState }>('/me/erasure-request/confirm', { method: 'POST', body: JSON.stringify({ code }) })).data;
  }

  /** Drops an open request: nothing is deleted. */
  async cancelErasure(): Promise<void> {
    await this.request('/me/erasure-request', { method: 'DELETE' });
  }

  /** Downloads everything held about the signed-in person (and their children, for a parent) as a zip. */
  async downloadMyData(): Promise<void> {
    const response = await this.authFetch(`${this.baseUrl}/me/export`, {
      headers: this.token ? { Authorization: `Bearer ${this.token}` } : {},
    });

    if (response.status === 401) {
      this.handleUnauthorized();
      return;
    }
    if (response.status === 429) {
      throw new Error(i18n.t('legal:myData.errors.tooMany'));
    }
    if (!response.ok) {
      throw new Error(i18n.t('legal:myData.errors.generic'));
    }

    const blob = await response.blob();
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `keiko-data-${new Date().toISOString().slice(0, 10).replace(/-/g, '')}.zip`;
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
  }

  /** Fetches a report PDF as a blob: URL for inline preview (an <iframe> or <embed>). Caller must revoke it when done. */
  async previewReportPdf(endpoint: string): Promise<string> {
    return this.fileUrl(endpoint, i18n.t('common:errors.reportDownload'));
  }

  /**
   * A private API file as a blob: URL: used for what lives under var/ and only
   * leaves through an endpoint (a club's registration files, a form a club sent
   * to be integrated). The API reads the token from a header, so a plain <a
   * href> answers 401 — callers fetch the blob and open it. Caller revokes the URL.
   */
  async fileUrl(endpoint: string, errorMessage: string = i18n.t('common:errors.fileOpen')): Promise<string> {
    const response = await this.authFetch(`${this.baseUrl}${endpoint}`, {
      headers: this.token ? { Authorization: `Bearer ${this.token}` } : {},
    });

    if (response.status === 401) {
      this.handleUnauthorized();
      throw new Error(errorMessage);
    }
    if (!response.ok) {
      throw new Error(errorMessage);
    }

    const blob = await response.blob();
    return URL.createObjectURL(blob);
  }

  // --- Attendance (coach) ---------------------------------------------------

  async getTodayOccurrences(date?: string): Promise<{ date: string; occurrences: ApiOccurrenceSummary[] }> {
    const query = date ? `?date=${date}` : '';
    const response = await this.request<{ success: boolean; data: { date: string; occurrences: ApiOccurrenceSummary[] } }>(`/attendance/today${query}`);
    return response.data;
  }

  async getClassDays(from: string, to: string): Promise<ApiClassDays> {
    const response = await this.request<{ success: boolean; data: ApiClassDays }>(`/attendance/class-days?from=${from}&to=${to}`);
    return response.data;
  }

  async getOccurrenceDetail(id: number): Promise<ApiOccurrenceDetail> {
    const response = await this.request<{ success: boolean; data: ApiOccurrenceDetail }>(`/attendance/occurrences/${id}`);
    return response.data;
  }

  async markAttendance(occurrenceId: number, studentId: number, status: AttendanceStatus): Promise<{ studentId: number; status: AttendanceStatus }> {
    const response = await this.request<{ success: boolean; data: { studentId: number; status: AttendanceStatus } }>(
      `/attendance/occurrences/${occurrenceId}/students/${studentId}`,
      { method: 'PUT', body: JSON.stringify({ status }) }
    );
    return response.data;
  }

  async markAllPresent(occurrenceId: number): Promise<void> {
    await this.request(`/attendance/occurrences/${occurrenceId}/mark-all-present`, { method: 'POST' });
  }

  async getAttendanceStats(classGroupId: number, studentId?: number): Promise<ApiAttendanceStats> {
    const params = new URLSearchParams({ classGroupId: String(classGroupId) });
    if (studentId) params.set('studentId', String(studentId));
    const response = await this.request<{ success: boolean; data: ApiAttendanceStats }>(`/attendance/stats?${params.toString()}`);
    return response.data;
  }

  async getAttendanceOverview(): Promise<ApiAttendanceOverview> {
    const response = await this.request<{ success: boolean; data: ApiAttendanceOverview }>('/attendance/overview');
    return response.data;
  }

  async getAttendanceDashboard(): Promise<ApiDashboardGroup[]> {
    const response = await this.request<{ success: boolean; data: { groups: ApiDashboardGroup[] } }>('/attendance/dashboard');
    return response.data.groups;
  }

  async getClassGroups(): Promise<ApiClassGroupRef[]> {
    return this.cachedRead('/class_groups', () => this.loadClassGroups());
  }

  /**
   * One class as a coach reads it: the level, its roster, its weekly slots and
   * its next classes (GET /api/coach/classes/{id}). The admin pages read the same
   * data from the admin-only endpoints; a coach cannot.
   */
  async getCoachClassDetail(classId: number): Promise<ApiCoachClassDetail> {
    const response = await this.request<{ success: boolean; data: ApiCoachClassDetail }>(`/coach/classes/${classId}`);
    return response.data;
  }

  private async loadClassGroups(): Promise<ApiClassGroupRef[]> {
    const response = await this.authFetch(`${this.baseUrl}/class_groups`, {
      headers: {
        'Content-Type': 'application/json',
        ...(this.token ? { Authorization: `Bearer ${this.token}` } : {}),
      },
    });
    if (response.status === 401) {
      this.handleUnauthorized();
      return [];
    }
    if (!response.ok) {
      return [];
    }
    const data = await response.json();
    const members = data.member || data['hydra:member'] || [];
    return members.map((m: any) => ({ id: m.id, name: m.name, color: m.color ?? null, description: m.description ?? null, minAge: m.minAge ?? null, maxAge: m.maxAge ?? null, activity: m.activity ?? null, programId: programIdOf(m.program), enrollment: m.enrollment ?? 'roster', bookingDefaults: m.bookingDefaults ?? null }));
  }

  async createClassGroup(name: string, description: string, color: string, clubId: string | number, ages: { minAge: number | null; maxAge: number | null } = { minAge: null, maxAge: null }, activityKey: string | null = null, programId: number | null = null, extra: Record<string, unknown> = {}): Promise<ApiClassGroupRef> {
    const response = await this.authFetch(`${this.baseUrl}/class_groups`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(this.token ? { Authorization: `Bearer ${this.token}` } : {}),
      },
      body: JSON.stringify({ name, description, color, ...ages, club: `/api/clubs/${clubId}`, ...(activityKey ? { activity: `/api/activities/${activityKey}` } : {}), ...(programId ? { program: `/api/programs/${programId}` } : {}), ...extra }),
    });
    if (response.status === 401) {
      this.handleUnauthorized();
      throw new Error('Unauthorized');
    }
    if (!response.ok) {
      const errorData = await response.json().catch(() => null);
      throw new Error(errorData?.['hydra:description'] || errorData?.detail || 'Failed to create class group');
    }
    const m = await response.json();
    return { id: m.id, name: m.name, color: m.color ?? null, description: m.description ?? null, minAge: m.minAge ?? null, maxAge: m.maxAge ?? null, activity: m.activity ?? null, programId: programIdOf(m.program), enrollment: m.enrollment ?? 'roster', bookingDefaults: m.bookingDefaults ?? null };
  }

  async deleteClassGroup(id: number | string, force = false): Promise<void> {
    const response = await this.authFetch(`${this.baseUrl}/class_groups/${id}${force ? '?force=true' : ''}`, {
      method: 'DELETE',
      headers: {
        ...(this.token ? { Authorization: `Bearer ${this.token}` } : {}),
      },
    });
    if (response.status === 401) {
      this.handleUnauthorized();
      return;
    }
    if (!response.ok && response.status !== 204) {
      const errorData = await response.json().catch(() => null);
      throw new ApiHttpError(errorData?.['hydra:description'] || errorData?.detail || 'Failed to delete class level', response.status);
    }
  }

  // --- Attendance (student) --------------------------------------------------

  async getMyHome(): Promise<ApiMemberHome> {
    const response = await this.request<{ success: boolean; data: ApiMemberHome }>('/me/home');
    return response.data;
  }

  async getMyAttendance(): Promise<ApiMyAttendance> {
    const response = await this.request<{ success: boolean; data: ApiMyAttendance }>('/attendance/my');
    return response.data;
  }

  async getTrainingStats(studentId?: number): Promise<ApiTrainingStats> {
    const query = studentId ? `?studentId=${studentId}` : '';
    const response = await this.request<{ success: boolean; data: ApiTrainingStats }>(`/me/training-history/stats${query}`);
    return response.data;
  }

  async getTrainingEntries(params: { studentId?: number; activity?: string | null; page?: number }): Promise<ApiTrainingEntries> {
    const query = new URLSearchParams();
    if (params.studentId) query.set('studentId', String(params.studentId));
    if (params.activity) query.set('activity', params.activity);
    query.set('page', String(params.page ?? 1));
    const response = await this.request<{ success: boolean; data: ApiTrainingEntries }>(`/me/training-history/entries?${query}`);
    return response.data;
  }

  async getStudentAttendance(studentId: number): Promise<ApiMyAttendance> {
    const response = await this.request<{ success: boolean; data: ApiMyAttendance }>(`/attendance/students/${studentId}`);
    return response.data;
  }

  // --- Admin: dashboard --------------------------------------------------

  /** The admin home in one call: counters, todo material, heatmap, last events (see AdminDashboardController). */
  async getAdminDashboard(): Promise<ApiAdminDashboard> {
    const response = await this.request<{ success: boolean; data: ApiAdminDashboard }>('/admin/dashboard');
    return response.data;
  }

  /** People of the club by name or address, for the header search: members first, then staff. */
  async searchAdminPeople(query: string): Promise<AdminSearchResult[]> {
    const response = await this.request<{ success: boolean; data: { results: AdminSearchResult[] } }>(
      `/admin/search?q=${encodeURIComponent(query)}`
    );
    return response.data.results;
  }

  // --- Admin: user management -------------------------------------------

  /** `staffOnly` leaves the club's members out: they are managed on /api/admin/students. */
  async getAdminUsers(page = 1, limit = 50, staffOnly = false): Promise<{ users: AdminUser[]; pagination: { page: number; limit: number; total: number; pages: number } }> {
    const response = await this.request<{ success: boolean; data: { users: AdminUser[]; pagination: { page: number; limit: number; total: number; pages: number } } }>(
      `/admin/users?page=${page}&limit=${limit}${staffOnly ? '&staffOnly=1' : ''}`
    );
    return response.data;
  }

  /** Adds a member to the active club. Active immediately; no email is sent (see inviteMember). */
  async createAdminUser(input: { email?: string | null; firstName: string; lastName: string; role: string; classGroupIds?: number[]; gender?: string | null; birthDate?: string | null; licenceNumber?: string; federation?: string }): Promise<AdminUser> {
    const response = await this.request<{ success: boolean; data: AdminUser }>('/admin/users', {
      method: 'POST',
      body: JSON.stringify(input),
    });
    return response.data;
  }

  /** Global roles and club are not editable: the role in this club, class levels, the person's login email (not your own) and, for accounts you created, the name. */
  async updateAdminUser(id: number, input: Partial<{ firstName: string; lastName: string; email: string; role: string; classGroupIds: number[]; gender: string | null; birthDate: string | null; signInAllowed: boolean | null; licenceNumber: string; federation: string }>): Promise<AdminUser> {
    const response = await this.request<{ success: boolean; data: AdminUser }>(`/admin/users/${id}`, {
      method: 'PUT',
      body: JSON.stringify(input),
    });
    return response.data;
  }

  /** Whether this child already has an account in another club (parent's address, name and birth date must all match). */
  async knownStudent(input: { parentEmail: string; firstName: string; lastName: string; birthDate: string }): Promise<{ found: boolean; clubs: string[]; possibleDuplicate: boolean }> {
    const params = new URLSearchParams(input);
    const response = await this.request<{ success: boolean; data: { found: boolean; clubs: string[]; possibleDuplicate: boolean } }>(`/admin/students/known?${params.toString()}`);
    return response.data;
  }

  /** A parent proves they know a child; a miss comes back as a 422 with `details.remaining`, a lock as a 423. */
  async confirmChild(firstName: string, lastName: string, birthDate: string): Promise<{ id: number; name: string }> {
    const response = await this.request<{ success: boolean; data: { child: { id: number; name: string } } }>('/me/confirm-child', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ firstName, lastName, birthDate }),
    });
    return response.data.child;
  }

  /** A guardian decides whether their child may sign in on their own (null = by age). */
  async setChildSignIn(childId: number, signInAllowed: boolean | null): Promise<{ id: number; signInAllowed: boolean | null; canSignIn: boolean }> {
    const response = await this.request<{ success: boolean; data: { id: number; signInAllowed: boolean | null; canSignIn: boolean } }>(`/me/children/${childId}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ signInAllowed }),
    });
    return response.data;
  }

  /** Removes the member from the active club; the person keeps their account. */
  async deleteAdminUser(id: number): Promise<void> {
    await this.request(`/admin/users/${id}`, { method: 'DELETE' });
  }

  /** Emails a login link to a member. Nothing is ever emailed automatically. */
  async inviteMember(membershipId: number): Promise<void> {
    await this.request(`/memberships/${membershipId}/invite`, { method: 'POST' });
  }

  /** Sets what a coach or admin may do beyond their role: permissions granted and denied. Answers with the updated member. */
  async setMembershipPermissions(membershipId: number, grant: Permission[], deny: Permission[]): Promise<AdminUser> {
    const response = await this.request<{ success: boolean; data: AdminUser }>(`/memberships/${membershipId}/permissions`, {
      method: 'PUT',
      body: JSON.stringify({ grant, deny }),
    });
    return response.data;
  }

  /** A staff member starts (`on`) or stops training in the club; stopping revokes their codes and class levels. */
  async setMembershipMember(membershipId: number, on: boolean): Promise<AdminUser> {
    const response = await this.request<{ success: boolean; data: AdminUser }>(`/memberships/${membershipId}/member`, {
      method: on ? 'POST' : 'DELETE',
    });
    return response.data;
  }

  /** The signed-in person's notification settings, one entry per club where their role offers any. */
  async getMyNotifications(): Promise<NotificationSettings[]> {
    const response = await this.request<{ success: boolean; data: NotificationSettings[] }>('/me/notifications');
    return response.data;
  }

  /**
   * Subscribes, changes the frequency, or switches the type off (a null
   * frequency). `scope` is a membership id, or "platform" for the console.
   */
  async setMyNotification(scope: number | 'platform', type: NotificationTypeKey, frequency: NotificationFrequency | null): Promise<NotificationSettings> {
    const response = await this.request<{ success: boolean; data: NotificationSettings }>(`/me/notifications/${scope}`, {
      method: 'PUT',
      body: JSON.stringify({ type, channel: 'email', frequency }),
    });
    return response.data;
  }

  /** A staff member's notification settings in the active club (owners and admins). */
  async getStaffNotifications(membershipId: number): Promise<NotificationSettings> {
    const response = await this.request<{ success: boolean; data: NotificationSettings }>(`/memberships/${membershipId}/notifications`);
    return response.data;
  }

  async setStaffNotification(membershipId: number, type: NotificationTypeKey, frequency: NotificationFrequency | null): Promise<NotificationSettings> {
    const response = await this.request<{ success: boolean; data: NotificationSettings }>(`/memberships/${membershipId}/notifications`, {
      method: 'PUT',
      body: JSON.stringify({ type, channel: 'email', frequency }),
    });
    return response.data;
  }

  async transferOwnership(membershipId: number): Promise<void> {
    await this.request('/club/transfer-ownership', { method: 'POST', body: JSON.stringify({ membershipId }) });
  }

  // --- Admin: student management -----------------------------------------

  /** `includeInactive`: the admin list also shows disabled students; rosters and counts leave it off. */
  async getAdminStudents(classGroupId?: number, page = 1, limit = 200, includeInactive = false): Promise<{ students: AdminStudent[]; pagination: { page: number; limit: number; total: number; pages: number } }> {
    const params = new URLSearchParams({ page: String(page), limit: String(limit) });
    if (classGroupId) params.set('classGroupId', String(classGroupId));
    if (includeInactive) params.set('includeInactive', '1');
    const response = await this.request<{ success: boolean; data: { students: AdminStudent[]; pagination: { page: number; limit: number; total: number; pages: number } } }>(
      `/admin/students?${params.toString()}`
    );
    return response.data;
  }

  /** Pauses a student: still listed, no longer counted in rosters, headcounts or at the devices. */
  async disableStudent(studentId: number): Promise<AdminStudent> {
    const response = await this.request<{ success: boolean; data: AdminStudent }>(`/admin/students/${studentId}/disable`, { method: 'POST' });
    return response.data;
  }

  async enableStudent(studentId: number): Promise<AdminStudent> {
    const response = await this.request<{ success: boolean; data: AdminStudent }>(`/admin/students/${studentId}/enable`, { method: 'POST' });
    return response.data;
  }

  /** Records the member's licence number in one federation for the club's current season; returns the member with their licences. */
  async setStudentLicence(studentId: number, licenceNumber: string, federation: string): Promise<AdminStudent> {
    const response = await this.request<{ success: boolean; data: AdminStudent }>(`/admin/students/${studentId}/licence`, {
      method: 'PUT',
      body: JSON.stringify({ licenceNumber, federation }),
    });
    return response.data;
  }

  /** The federations the club handles and the season they are licensed for: the licence inputs of the member forms. */
  async getLicenceFederations(): Promise<{ season: string; federations: ClubFederation[] }> {
    const response = await this.request<{ success: boolean; data: { season: string; federations: ClubFederation[] } }>('/admin/licence-federations');
    return response.data;
  }

  /** Same as setStudentLicence for anyone of the club (staff, associates); returns the person with their licences. */
  async setUserLicence(userId: number, licenceNumber: string, federation: string): Promise<AdminUser> {
    const response = await this.request<{ success: boolean; data: AdminUser }>(`/admin/users/${userId}/licence`, {
      method: 'PUT',
      body: JSON.stringify({ licenceNumber, federation }),
    });
    return response.data;
  }

  async getAdminStudent(studentId: number): Promise<AdminStudent> {
    const response = await this.request<{ success: boolean; data: AdminStudent }>(`/admin/students/${studentId}`);
    return response.data;
  }

  /** `assignClasses` also adds each student to the classes matching their age (age band) and activities. */
  /** dryRun: the same rules run and every row's verdict comes back, nothing is written. */
  async importStudents(classGroupId: number | null, students: ImportStudentRow[], assignClasses = false, dryRun = false): Promise<ImportStudentsResult> {
    const response = await this.request<{ success: boolean; data: ImportStudentsResult }>('/admin/students/import', {
      method: 'POST',
      body: JSON.stringify({ classGroupId, students, assignClasses, dryRun }),
    });
    return response.data;
  }

  /** Links a parent (found or created by email) to a student; returns the refreshed student. */
  async addStudentGuardian(studentId: number, guardian: { firstName: string; lastName?: string; email: string; sendInvite?: boolean }): Promise<AdminStudent> {
    const response = await this.request<{ success: boolean; data: AdminStudent }>(`/admin/students/${studentId}/guardians`, {
      method: 'POST',
      body: JSON.stringify(guardian),
    });
    return response.data;
  }

  async removeStudentGuardian(studentId: number, guardianshipId: number): Promise<AdminStudent> {
    const response = await this.request<{ success: boolean; data: AdminStudent }>(`/admin/students/${studentId}/guardians/${guardianshipId}`, { method: 'DELETE' });
    return response.data;
  }

  /** From the consent age, a confirmed parent gives the child their own login; the child gets a link by email. */
  async releaseChild(childId: number, email: string): Promise<void> {
    await this.request(`/me/children/${childId}/release`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email }),
    });
  }

  /** The same, done by a club admin at the family's request. */
  async releaseStudent(studentId: number, email: string): Promise<AdminStudent> {
    const response = await this.request<{ success: boolean; data: AdminStudent }>(`/admin/students/${studentId}/release`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email }),
    });
    return response.data;
  }

  /** Gives a locked-out parent new tries to confirm the child. */
  async resetGuardianLock(studentId: number, guardianshipId: number): Promise<AdminStudent> {
    const response = await this.request<{ success: boolean; data: AdminStudent }>(`/admin/students/${studentId}/guardians/${guardianshipId}/reset-lock`, { method: 'POST' });
    return response.data;
  }

  /** Changes a parent's address at their request: global, and every child goes back to pending. */
  async changeStudentGuardianEmail(studentId: number, guardianshipId: number, email: string): Promise<AdminStudent> {
    const response = await this.request<{ success: boolean; data: AdminStudent }>(`/admin/students/${studentId}/guardians/${guardianshipId}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email }),
    });
    return response.data;
  }

  async inviteStudentGuardian(studentId: number, guardianshipId: number): Promise<void> {
    await this.request(`/admin/students/${studentId}/guardians/${guardianshipId}/invite`, { method: 'POST' });
  }

  // --- Member info sheet ---------------------------------------------------

  async getMemberInfoSheet(studentId: number): Promise<MemberInfoSheet> {
    const response = await this.request<{ success: boolean; data: MemberInfoSheet }>(`/admin/students/${studentId}/info-sheet`);
    return response.data;
  }

  /** Removes the member and erases what the club holds about them (registrations and files, attendance, progress, consents). Cannot be undone. */
  async eraseStudentFromClub(studentId: number): Promise<{ id: number; accountDeleted: boolean }> {
    const response = await this.request<{ success: boolean; data: { id: number; accountDeleted: boolean } }>(`/admin/students/${studentId}/erase`, {
      method: 'POST',
      body: JSON.stringify({ confirm: true }),
    });
    return response.data;
  }

  async setMemberEmergencyContacts(studentId: number, contacts: EmergencyContact[]): Promise<MemberInfoSheet> {
    const response = await this.request<{ success: boolean; data: MemberInfoSheet }>(`/admin/students/${studentId}/emergency-contacts`, {
      method: 'PUT',
      body: JSON.stringify({ contacts }),
    });
    return response.data;
  }

  // --- Attendance device codes ---------------------------------------------

  /** Replaces the student's QR (a lost card): the old code stops working. */
  async regenerateStudentQr(studentId: number): Promise<StudentCredentials> {
    const response = await this.request<{ success: boolean; data: StudentCredentials }>(`/admin/students/${studentId}/credentials/qr/regenerate`, { method: 'POST' });
    return response.data;
  }

  async assignStudentRfid(studentId: number, code: string): Promise<StudentCredentials> {
    const response = await this.request<{ success: boolean; data: StudentCredentials }>(`/admin/students/${studentId}/credentials/rfid`, {
      method: 'PUT',
      body: JSON.stringify({ code }),
    });
    return response.data;
  }

  async revokeStudentRfid(studentId: number): Promise<StudentCredentials> {
    const response = await this.request<{ success: boolean; data: StudentCredentials }>(`/admin/students/${studentId}/credentials/rfid`, { method: 'DELETE' });
    return response.data;
  }

  /** The signed-in student's own QR (or the child's, for a parent acting as them). */
  async getMyCard(): Promise<ApiMyCard | null> {
    const response = await this.request<{ success: boolean; data: ApiMyCard | null }>('/me/card');
    return response.data;
  }

  async assignGradeToStudents(studentIds: number[], gradeId: number, programId?: number): Promise<{ updated: number; students: AdminStudent[] }> {
    const response = await this.request<{ success: boolean; data: { updated: number; students: AdminStudent[] } }>('/admin/students/assign-grade', {
      method: 'POST',
      body: JSON.stringify({ studentIds, gradeId, programId }),
    });
    return response.data;
  }

  /** Removes several students from the club at once; like deleteAdminUser, their account survives. */
  async deleteStudents(studentIds: number[]): Promise<{ deleted: number; deletedIds: number[]; skippedIds: number[] }> {
    const response = await this.request<{ success: boolean; data: { deleted: number; deletedIds: number[]; skippedIds: number[] } }>('/admin/students/batch-delete', {
      method: 'POST',
      body: JSON.stringify({ studentIds }),
    });
    return response.data;
  }

  async enrollStudent(studentId: number, programId: number, gradeId?: number | null): Promise<AdminStudent> {
    const response = await this.request<{ success: boolean; data: AdminStudent }>(`/admin/students/${studentId}/enrollments`, {
      method: 'POST',
      body: JSON.stringify({ programId, gradeId: gradeId ?? null }),
    });
    return response.data;
  }

  async updateEnrollment(studentId: number, enrollmentId: number, gradeId: number | null): Promise<AdminStudent> {
    const response = await this.request<{ success: boolean; data: AdminStudent }>(`/admin/students/${studentId}/enrollments/${enrollmentId}`, {
      method: 'PATCH',
      body: JSON.stringify({ gradeId }),
    });
    return response.data;
  }

  async deleteEnrollment(studentId: number, enrollmentId: number): Promise<AdminStudent> {
    const response = await this.request<{ success: boolean; data: AdminStudent }>(`/admin/students/${studentId}/enrollments/${enrollmentId}`, {
      method: 'DELETE',
    });
    return response.data;
  }

  async getMyClubs(): Promise<{ id: number; name: string; description: string | null; logoUrl: string | null; brandingColors: Record<string, string> | null; timezone: string | null; role?: ClubRoleName }[]> {
    type Club = { id: number; name: string; description: string | null; logoUrl: string | null; brandingColors: Record<string, string> | null; timezone: string | null; role?: ClubRoleName };
    // API Platform serves this route as a hydra collection (`member`); the controller shape (`data`) is kept as a fallback.
    const response = await this.request<{ data?: Club[]; member?: Club[] }>('/clubs');
    return response.member ?? response.data ?? [];
  }

  /** The owner records that they checked who on the team still needs access (owner only). */
  async markAccessReviewed(clubId: number | string): Promise<void> {
    await this.request(`/admin/clubs/${clubId}/access-review`, { method: 'POST' });
  }

  /** The club owner accepts the data processing agreement for the club (owner only). */
  async acceptClubDpa(clubId: number | string, input: { version: string; role: string; authorised: boolean }): Promise<void> {
    await this.request(`/admin/clubs/${clubId}/dpa`, { method: 'POST', body: JSON.stringify(input) });
  }

  /** The active club, as a one-item list (clubs are created by super admins, never here). */
  async getAdminClubs(): Promise<AdminClub[]> {
    const user = await this.getCurrentUser();
    if (!user?.club?.id) return [];
    const response = await this.request<{ data?: AdminClub } & Partial<AdminClub>>(`/clubs/${user.club.id}`);
    const club = (response.data ?? response) as AdminClub;
    return club?.id ? [club] : [];
  }

  /** Moves this browser session to another club the user actively belongs to. */
  async switchClub(clubId: number): Promise<User> {
    const response: LoginResponse = await this.request('/auth/switch-club', {
      method: 'POST',
      body: JSON.stringify({ clubId }),
    });
    this.token = response.data.token;
    localStorage.setItem('token', this.token);
    localStorage.setItem('user', JSON.stringify(response.data.user));
    return response.data.user;
  }

  /** Reloads /api/me (roles, memberships, impersonation state) into local storage. */
  async refreshCurrentUser(): Promise<User | null> {
    const response = await this.request<{ success: boolean; data: User }>('/me');
    if (response.success) {
      localStorage.setItem('user', JSON.stringify(response.data));
      return response.data;
    }
    return null;
  }

  // --- Admin: audit log ---------------------------------------------------

  async getAuditLog(filters: { page?: number; limit?: number; actorId?: number; action?: string; entityClass?: string; from?: string; to?: string } = {}): Promise<{ entries: AuditLogEntry[]; pagination: { page: number; limit: number; total: number; pages: number } }> {
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') params.set(key, String(value));
    });
    const response = await this.request<{ success: boolean; data: { entries: AuditLogEntry[]; pagination: { page: number; limit: number; total: number; pages: number } } }>(
      `/admin/audit-log?${params.toString()}`
    );
    return response.data;
  }

  async getAuditLogEntityTypes(): Promise<AuditLogEntityType[]> {
    const response = await this.request<{ success: boolean; data: AuditLogEntityType[] }>('/admin/audit-log/entity-types');
    return response.data;
  }

  async updateAdminClub(id: number, input: Partial<{ name: string; description: string; address: string; phone: string; email: string; website: string; timezone: string; defaultLanguage: string; mailFromName: string | null; mailFromAddress: string | null; digitalConsentAge: number | null; privacyNotice: string | null; retention: { abandonedMonths: number; filesMonths: number; registrationsMonths: number }; monthlyParentReportsEnabled: boolean; features: Partial<Record<ClubSwitch, boolean>> }>): Promise<AdminClub> {
    const response = await this.request<{ success: boolean; data: AdminClub }>(`/admin/clubs/${id}`, {
      method: 'PUT',
      body: JSON.stringify(input),
    });
    return response.data;
  }

  /**
   * The club's stored sender next to the platform default, plus the sender that
   * would really be used today (a stored address on a domain the platform no
   * longer authorises falls back to the platform address).
   */
  async getClubEmailSender(id: number): Promise<ClubEmailSender> {
    const response = await this.request<{ success: boolean; data: ClubEmailSender }>(`/admin/clubs/${id}/email-sender`);
    return response.data;
  }

  async uploadClubLogo(id: number, file: File): Promise<AdminClub> {
    const formData = new FormData();
    formData.append('logo', file);
    const response = await this.authFetch(`${this.baseUrl}/admin/clubs/${id}/upload-logo`, {
      method: 'POST',
      headers: this.token ? { Authorization: `Bearer ${this.token}` } : {},
      body: formData,
    });
    if (response.status === 401) {
      this.handleUnauthorized();
      throw new Error('Unauthorized');
    }
    if (!response.ok) {
      const errorData = await response.json().catch(() => null);
      throw new Error(errorData?.error?.message || 'Failed to upload logo');
    }
    const data = await response.json();
    return data.data;
  }

  // --- Admin: class levels -------------------------------------------------

  async updateClassGroup(id: number | string, input: Partial<{ name: string; description: string; color: string; minAge: number | null; maxAge: number | null; activity: string; program: string | null; enrollment: 'roster' | 'booking'; bookingDefaults: BookingSettings | null }>): Promise<ApiClassGroupRef> {
    const response = await this.authFetch(`${this.baseUrl}/class_groups/${id}`, {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/merge-patch+json',
        ...(this.token ? { Authorization: `Bearer ${this.token}` } : {}),
      },
      body: JSON.stringify(input),
    });
    if (response.status === 401) {
      this.handleUnauthorized();
      throw new Error('Unauthorized');
    }
    if (!response.ok) {
      const errorData = await response.json().catch(() => null);
      throw new Error(errorData?.['hydra:description'] || errorData?.detail || 'Failed to update class level');
    }
    const m = await response.json();
    return { id: m.id, name: m.name, color: m.color ?? null, description: m.description ?? null, minAge: m.minAge ?? null, maxAge: m.maxAge ?? null, activity: m.activity ?? null, programId: programIdOf(m.program), enrollment: m.enrollment ?? 'roster', bookingDefaults: m.bookingDefaults ?? null };
  }

  /** The federation catalog (France Judo, FFK...). */
  async getFederations(): Promise<{ key: string; acronym: string; name: string; disciplines: string | null; logo: string | null; color: string; portalUrl: string | null; importAvailable: boolean }[]> {
    const data = await this.request<any>('/federations');
    return Array.isArray(data) ? data : data.member ?? data['hydra:member'] ?? [];
  }

  // --- Club activities ----------------------------------------------------

  /** The whole catalog, each entry marked with whether the active club offers it. */
  async getClubActivities(): Promise<ApiClubActivity[]> {
    const response = await this.request<{ data: { activities: ApiClubActivity[] } }>('/admin/activities');
    return response.data.activities;
  }

  async offerActivities(keys: string[]): Promise<ApiClubActivity[]> {
    const response = await this.request<{ data: { activities: ApiClubActivity[] } }>('/admin/activities', {
      method: 'POST',
      body: JSON.stringify({ keys }),
    });
    return response.data.activities;
  }

  /**
   * Creates an activity of the club's own and offers it. A catalog name is a 422
   * whose `details.catalogKey` names the catalog activity. `key` is the created
   * activity, so the screen can point the admin at it.
   */
  async createCustomActivity(input: { name: string; family: string; description?: string }): Promise<{ activities: ApiClubActivity[]; key: string }> {
    const response = await this.request<{ data: { activities: ApiClubActivity[]; key: string } }>('/admin/activities/custom', {
      method: 'POST',
      body: JSON.stringify(input),
    });
    return { activities: response.data.activities, key: response.data.key };
  }

  async updateCustomActivity(key: string, input: { name?: string; family?: string; description?: string }): Promise<ApiClubActivity[]> {
    const response = await this.request<{ data: { activities: ApiClubActivity[] } }>(`/admin/activities/${key}`, {
      method: 'PATCH',
      body: JSON.stringify(input),
    });
    return response.data.activities;
  }

  async setActivityDisabledFeatures(key: string, disabledFeatures: string[]): Promise<ApiClubActivity[]> {
    const response = await this.request<{ data: { activities: ApiClubActivity[] } }>(`/admin/activities/${key}`, {
      method: 'PATCH',
      body: JSON.stringify({ disabledFeatures }),
    });
    return response.data.activities;
  }

  /** Hides the catalog tags listed here; the others stay available. */
  async setActivityDisabledTags(key: string, disabledTags: string[]): Promise<ApiClubActivity[]> {
    const response = await this.request<{ data: { activities: ApiClubActivity[] } }>(`/admin/activities/${key}`, {
      method: 'PATCH',
      body: JSON.stringify({ disabledTags }),
    });
    return response.data.activities;
  }

  /** Adds a tag of the club's own to one of its activities. */
  async createActivityTag(key: string, name: string): Promise<ApiClubActivity[]> {
    const response = await this.request<{ data: { activities: ApiClubActivity[] } }>(`/admin/activities/${key}/tags`, {
      method: 'POST',
      body: JSON.stringify({ name }),
    });
    return response.data.activities;
  }

  /** Renames a club tag or switches it on and off. */
  async updateActivityTag(key: string, id: number, input: { name?: string; enabled?: boolean }): Promise<ApiClubActivity[]> {
    const response = await this.request<{ data: { activities: ApiClubActivity[] } }>(`/admin/activities/${key}/tags/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(input),
    });
    return response.data.activities;
  }

  /** Deleting a tag drops it from the classes that carried it, on read. */
  async deleteActivityTag(key: string, id: number): Promise<ApiClubActivity[]> {
    const response = await this.request<{ data: { activities: ApiClubActivity[] } }>(`/admin/activities/${key}/tags/${id}`, { method: 'DELETE' });
    return response.data.activities;
  }

  /** The club's number in a federation; digits only, empty clears it. */
  async saveAffiliation(federationKey: string, clubNumber: string): Promise<ApiClubActivity[]> {
    const response = await this.request<{ data: { activities: ApiClubActivity[] } }>(`/admin/affiliations/${federationKey}`, {
      method: 'PUT',
      body: JSON.stringify({ clubNumber }),
    });
    return response.data.activities;
  }

  async withdrawActivity(key: string): Promise<ApiClubActivity[]> {
    const response = await this.request<{ data: { activities: ApiClubActivity[] } }>(`/admin/activities/${key}`, { method: 'DELETE' });
    return response.data.activities;
  }

  // --- Programs ---------------------------------------------------------

  async getPrograms(): Promise<ApiProgram[]> {
    return this.cachedRead('/programs', () => this.loadPrograms());
  }

  private async loadPrograms(): Promise<ApiProgram[]> {
    const response = await this.authFetch(`${this.baseUrl}/programs`, {
      headers: { ...(this.token ? { Authorization: `Bearer ${this.token}` } : {}) },
    });
    if (response.status === 401) {
      this.handleUnauthorized();
      return [];
    }
    if (!response.ok) return [];
    const data = await response.json();
    return data.member || data['hydra:member'] || [];
  }

  async getProgramUsage(): Promise<Record<string, number>> {
    const response = await this.request<{ success: boolean; data: { usage: Record<string, number> } }>('/programs/usage');
    return response.data.usage;
  }

  async getOfficialPrograms(): Promise<ApiOfficialProgramSummary[]> {
    const response = await this.request<{ success: boolean; data: { programs: ApiOfficialProgramSummary[] } }>('/programs/official');
    return response.data.programs;
  }

  /** A program on an official base: its grades come from the base, its syllabus is copied or started empty. */
  async createProgram(input: { baseKey: string; name?: string; copySyllabus: boolean; hiddenGradeKeys?: string[] }): Promise<ApiProgram> {
    const response = await this.request<{ success: boolean; data: ApiProgram }>('/programs', {
      method: 'POST',
      body: JSON.stringify(input),
    });
    return response.data;
  }

  /** A program the club writes itself for one of its activities: no grades, a flat syllabus. */
  async createBlankProgram(input: { activityKey: string; name: string }): Promise<ApiProgram> {
    const response = await this.request<{ success: boolean; data: ApiProgram }>('/programs', {
      method: 'POST',
      body: JSON.stringify(input),
    });
    return response.data;
  }

  /** The program's belts and steps: what the club may hide and what it hides (admin). */
  async getBeltSettings(programId: number | string): Promise<ApiBeltSettings> {
    const response = await this.request<{ success: boolean; data: ApiBeltSettings }>(`/programs/${programId}/belts`);
    return response.data;
  }

  async updateBeltSettings(programId: number | string, hiddenGradeKeys: string[]): Promise<ApiBeltSettings> {
    const response = await this.request<{ success: boolean; data: ApiBeltSettings }>(
      `/programs/${programId}/belts`,
      { method: 'PUT', body: JSON.stringify({ hiddenGradeKeys }) }
    );
    return response.data;
  }

  async updateProgram(id: number | string, input: Partial<{ name: string; customName: string | null; enabled: boolean; discipline: string; description: string | null; categories: { key: string; name: string }[]; hiddenGradeKeys: string[] }>): Promise<ApiProgram> {
    const response = await this.authFetch(`${this.baseUrl}/programs/${id}`, {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/merge-patch+json',
        ...(this.token ? { Authorization: `Bearer ${this.token}` } : {}),
      },
      body: JSON.stringify(input),
    });
    if (response.status === 401) {
      this.handleUnauthorized();
      throw new Error('Unauthorized');
    }
    if (!response.ok) {
      const errorData = await response.json().catch(() => null);
      throw new Error(errorData?.['hydra:description'] || errorData?.detail || 'Failed to update program');
    }
    return response.json();
  }

  async deleteProgram(id: number | string, force = false): Promise<void> {
    const response = await this.authFetch(`${this.baseUrl}/programs/${id}${force ? '?force=true' : ''}`, {
      method: 'DELETE',
      headers: { ...(this.token ? { Authorization: `Bearer ${this.token}` } : {}) },
    });
    if (response.status === 401) {
      this.handleUnauthorized();
      return;
    }
    if (!response.ok && response.status !== 204) {
      const errorData = await response.json().catch(() => null);
      throw new ApiHttpError(errorData?.['hydra:description'] || errorData?.detail || 'Failed to delete program', response.status);
    }
  }

  async syncProgram(id: number | string): Promise<{ categories: number; items: number; recategorized: number }> {
    const response = await this.request<{ success: boolean; data: { categories: number; items: number; recategorized: number } }>(`/programs/${id}/sync`, { method: 'POST' });
    return response.data;
  }

  async getProgramTree(id: number | string): Promise<{ program: ApiProgram; allGrades: (ApiGrade & { hidden: boolean })[]; grades: (ApiGrade & { items: ApiCurriculumItem[] })[]; items: ApiCurriculumItem[] }> {
    const response = await this.request<{ success: boolean; data: { program: ApiProgram; allGrades: (ApiGrade & { hidden: boolean })[]; grades: (ApiGrade & { items: ApiCurriculumItem[] })[]; items: ApiCurriculumItem[] } }>(`/programs/${id}/tree`);
    return response.data;
  }

  async reorderItems(programId: number | string, gradeId: number | string, itemIds: number[]): Promise<void> {
    await this.request(`/programs/${programId}/grades/${gradeId}/items/reorder`, { method: 'POST', body: JSON.stringify({ itemIds }) });
  }

  async uploadCurriculumImage(programId: number | string, file: File): Promise<{ url: string }> {
    const formData = new FormData();
    formData.append('image', file);
    const response = await this.authFetch(`${this.baseUrl}/programs/${programId}/images`, {
      method: 'POST',
      headers: this.token ? { Authorization: `Bearer ${this.token}` } : {},
      body: formData,
    });
    if (response.status === 401) {
      this.handleUnauthorized();
      throw new Error('Unauthorized');
    }
    if (!response.ok) {
      const errorData = await response.json().catch(() => null);
      throw new Error(errorData?.error?.message || 'Failed to upload image');
    }
    const data = await response.json();
    return data.data;
  }

  // --- Admin: curriculum items -----------------------------------------

  async createCurriculumItem(input: { programId: string | number; gradeId?: string | number | null; type: string; category: string; japaneseName: string; translationFr: string; externalId?: string | null; videoUrl?: string | null; content?: RichContent; order?: number }): Promise<ApiCurriculumItem> {
    const response = await this.authFetch(`${this.baseUrl}/curriculum_items`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(this.token ? { Authorization: `Bearer ${this.token}` } : {}),
      },
      body: JSON.stringify({
        program: `/api/programs/${input.programId}`,
        ...(input.gradeId ? { grade: `/api/grades/${input.gradeId}` } : {}),
        type: input.type,
        category: input.category,
        japaneseName: input.japaneseName,
        translationFr: input.translationFr,
        externalId: input.externalId ?? null,
        videoUrl: input.videoUrl ?? null,
        content: input.content ?? null,
        order: input.order ?? 0,
      }),
    });
    if (response.status === 401) {
      this.handleUnauthorized();
      throw new Error('Unauthorized');
    }
    if (!response.ok) {
      const errorData = await response.json().catch(() => null);
      throw new Error(errorData?.['hydra:description'] || errorData?.detail || 'Failed to create item');
    }
    return response.json();
  }

  async updateCurriculumItem(id: number | string, input: Partial<{ type: string; category: string; japaneseName: string; translationFr: string; externalId: string | null; videoUrl: string | null; content: RichContent; order: number }>): Promise<ApiCurriculumItem> {
    const response = await this.authFetch(`${this.baseUrl}/curriculum_items/${id}`, {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/merge-patch+json',
        ...(this.token ? { Authorization: `Bearer ${this.token}` } : {}),
      },
      body: JSON.stringify(input),
    });
    if (response.status === 401) {
      this.handleUnauthorized();
      throw new Error('Unauthorized');
    }
    if (!response.ok) {
      const errorData = await response.json().catch(() => null);
      throw new Error(errorData?.['hydra:description'] || errorData?.detail || 'Failed to update item');
    }
    return response.json();
  }

  async deleteCurriculumItem(id: number | string): Promise<void> {
    const response = await this.authFetch(`${this.baseUrl}/curriculum_items/${id}`, {
      method: 'DELETE',
      headers: { ...(this.token ? { Authorization: `Bearer ${this.token}` } : {}) },
    });
    if (response.status === 401) {
      this.handleUnauthorized();
      return;
    }
    if (!response.ok && response.status !== 204) {
      const errorData = await response.json().catch(() => null);
      throw new Error(errorData?.['hydra:description'] || errorData?.detail || 'Failed to delete item');
    }
  }

  // --- Badges: the club's library ----------------------------------------

  /** Every badge of the club, archived included, with how many people hold it. */
  async getBadges(): Promise<ApiBadgeLibrary> {
    const response = await this.request<{ data: ApiBadgeLibrary }>('/admin/badges');
    return response.data;
  }

  /** One badge with every award it ever received (the drawer's holder list). */
  async getBadge(id: number): Promise<ApiBadgeDefinition & { awards: ApiBadgeAward[] }> {
    const response = await this.request<{ data: ApiBadgeDefinition & { awards: ApiBadgeAward[] } }>(`/admin/badges/${id}`);
    return response.data;
  }

  /** Creates a draft; send a file to upload a picture, or source=club_logo to copy the club's. */
  async createBadge(input: { label: string; description?: string | null; kind?: BadgeKind; color?: string; source?: "club_logo" }, image?: File | null): Promise<ApiBadgeDefinition> {
    const form = new FormData();
    form.append('label', input.label);
    if (input.description != null) form.append('description', input.description);
    if (input.kind) form.append('kind', input.kind);
    if (input.color) form.append('color', input.color);
    if (input.source) form.append('source', input.source);
    if (image) form.append('image', image);
    return this.multipart<{ data: ApiBadgeDefinition }>('/admin/badges', form).then((r) => r.data);
  }

  async updateBadge(id: number, input: { label?: string; description?: string | null; kind?: BadgeKind; color?: string }): Promise<ApiBadgeDefinition> {
    const response = await this.request<{ data: ApiBadgeDefinition }>(`/admin/badges/${id}`, { method: 'PATCH', body: JSON.stringify(input) });
    return response.data;
  }

  /** Replaces the picture: an uploaded file, or the club's logo. */
  async setBadgeImage(id: number, image?: File | null, source?: "club_logo"): Promise<ApiBadgeDefinition> {
    const form = new FormData();
    if (image) form.append('image', image);
    if (source) form.append('source', source);
    return this.multipart<{ data: ApiBadgeDefinition }>(`/admin/badges/${id}/image`, form).then((r) => r.data);
  }

  async duplicateBadge(id: number): Promise<ApiBadgeDefinition> {
    const response = await this.request<{ data: ApiBadgeDefinition }>(`/admin/badges/${id}/duplicate`, { method: 'POST' });
    return response.data;
  }

  async archiveBadge(id: number): Promise<ApiBadgeDefinition> {
    const response = await this.request<{ data: ApiBadgeDefinition }>(`/admin/badges/${id}/archive`, { method: 'POST' });
    return response.data;
  }

  async unarchiveBadge(id: number): Promise<ApiBadgeDefinition> {
    const response = await this.request<{ data: ApiBadgeDefinition }>(`/admin/badges/${id}/unarchive`, { method: 'POST' });
    return response.data;
  }

  async deleteBadge(id: number): Promise<void> {
    await this.request(`/admin/badges/${id}`, { method: 'DELETE' });
  }

  /** Who may be badged by hand, each flagged with why not when they already hold it. */
  async getBadgeCandidates(id: number, query = ""): Promise<{ candidates: ApiBadgeCandidate[]; awardable: boolean }> {
    const suffix = query.trim() ? `?q=${encodeURIComponent(query.trim())}` : "";
    const response = await this.request<{ data: { candidates: ApiBadgeCandidate[]; awardable: boolean } }>(`/admin/badges/${id}/candidates${suffix}`);
    return response.data;
  }

  /** `results` carries the place and category per membership id, for a competition badge. */
  async awardBadge(id: number, membershipIds: number[], results?: Record<number, BadgeResultInput>): Promise<ApiBadgeAwardResult> {
    const body = results
      ? { results: membershipIds.map((membershipId) => ({ membershipId, ...(results[membershipId] ?? { rank: null, division: null }) })) }
      : { membershipIds };
    const response = await this.request<{ data: ApiBadgeAwardResult }>(`/admin/badges/${id}/award`, {
      method: 'POST',
      body: JSON.stringify(body),
    });
    return response.data;
  }

  /** Corrects the place and category of a competition award while it is still revocable. */
  async setBadgeAwardResult(awardId: number, result: BadgeResultInput): Promise<ApiBadgeAward> {
    const response = await this.request<{ data: ApiBadgeAward }>(`/admin/badge-awards/${awardId}/result`, {
      method: 'PATCH',
      body: JSON.stringify(result),
    });
    return response.data;
  }

  async revokeBadgeAward(awardId: number, reason: BadgeRevokeReason, note?: string): Promise<ApiBadgeAward> {
    const response = await this.request<{ data: ApiBadgeAward }>(`/admin/badge-awards/${awardId}/revoke`, {
      method: 'POST',
      body: JSON.stringify({ reason, note }),
    });
    return response.data;
  }

  /** A member's badges as the home club sees them: from any issuer, not hidden. */
  async getMemberBadges(membershipId: number): Promise<ApiBadgeAward[]> {
    const response = await this.request<{ data: { badges: ApiBadgeAward[] } }>(`/admin/members/${membershipId}/badges`);
    return response.data.badges;
  }

  // --- Badges: the holder's own ------------------------------------------

  async getMyBadges(): Promise<ApiMyBadges> {
    const response = await this.request<{ data: ApiMyBadges }>('/me/badges');
    return response.data;
  }

  /** Hides one badge from the home club's member record (or shows it again). */
  async setMyBadgeHidden(awardId: number, hidden: boolean): Promise<ApiBadgeAward> {
    const response = await this.request<{ data: ApiBadgeAward }>(`/me/badges/${awardId}`, { method: 'PATCH', body: JSON.stringify({ hidden }) });
    return response.data;
  }

  // --- Events: what the club organises -----------------------------------

  /** Every event of the club, drafts and past ones included: the list groups them. */
  async getEvents(): Promise<ApiEventSummary[]> {
    const response = await this.request<{ data: { events: ApiEventSummary[] } }>('/admin/events');
    return response.data.events;
  }

  async getEvent(id: number): Promise<ApiEvent> {
    const response = await this.request<{ data: ApiEvent }>(`/admin/events/${id}`);
    return response.data;
  }

  /** Creates a draft; the details are filled in on the event's own screen. */
  async createEvent(input: {
    title: string;
    category: EventCategory;
    categoryLabel?: string | null;
    startDate: string;
    endDate?: string;
  }): Promise<ApiEvent> {
    const response = await this.request<{ data: ApiEvent }>('/admin/events', { method: 'POST', body: JSON.stringify(input) });
    return response.data;
  }

  /** Saves one tab: send only the fields that tab owns. */
  async updateEvent(id: number, input: Partial<{
    title: string;
    category: EventCategory;
    categoryLabel: string | null;
    startDate: string;
    endDate: string;
    description: RichContent | null;
    audience: ApiEventAudience | null;
    whatToBring: string | null;
    practicalInfo: string | null;
    locationId: number | null;
    status: EventStatus;
    visibility: EventVisibility;
    registrationMode: EventRegistrationMode;
    capacity: number | null;
    halfDayAttendance: boolean;
    priceLabel: string | null;
    badgeDefinitionId: number | null;
    awardRule: EventAwardRule;
    awardMinDays: number | null;
  }>): Promise<ApiEvent> {
    const response = await this.request<{ data: ApiEvent }>(`/admin/events/${id}`, { method: 'PATCH', body: JSON.stringify(input) });
    return response.data;
  }

  /** A draft nobody registered for can go; a published event is cancelled instead. */
  async deleteEvent(id: number): Promise<void> {
    await this.request(`/admin/events/${id}`, { method: 'DELETE' });
  }

  /** Saves the whole line-up, in order: the order is part of the programme. */
  async saveEventSpeakers(id: number, speakers: ApiEventSpeakerInput[]): Promise<ApiEvent> {
    const response = await this.request<{ data: ApiEvent }>(`/admin/events/${id}/speakers`, {
      method: 'PUT',
      body: JSON.stringify({ speakers }),
    });
    return response.data;
  }

  /** The poster: an image, or a PDF for the A4 format (page 1 becomes the preview). */
  async uploadEventFlyer(id: number, format: EventFlyerFormat, file: File): Promise<ApiEvent> {
    const form = new FormData();
    form.append('format', format);
    form.append('flyer', file);
    return this.multipart<{ data: ApiEvent }>(`/admin/events/${id}/flyer`, form).then((r) => r.data);
  }

  async removeEventFlyer(id: number): Promise<ApiEvent> {
    const response = await this.request<{ data: ApiEvent }>(`/admin/events/${id}/flyer`, { method: 'DELETE' });
    return response.data;
  }

  /** Saves the whole programme, one row per day, in order. */
  async saveEventDays(id: number, days: ApiEventDayInput[]): Promise<ApiEvent> {
    const response = await this.request<{ data: ApiEvent }>(`/admin/events/${id}/days`, {
      method: 'PUT',
      body: JSON.stringify({ days }),
    });
    return response.data;
  }

  /** Who signed up, for the Inscrits tab. */
  async getEventRegistrations(id: number): Promise<ApiEventRegistration[]> {
    const response = await this.request<{ data: { registrations: ApiEventRegistration[] } }>(`/admin/events/${id}/registrations`);
    return response.data.registrations;
  }

  /** One registration the club handles by hand: waitlist, paid on site, matched account. */
  async updateEventRegistration(
    id: number,
    registrationId: number,
    input: { status?: EventRegistrationStatus; paidOnSite?: boolean; userId?: number | null },
  ): Promise<ApiEventRegistration> {
    const response = await this.request<{ data: ApiEventRegistration }>(`/admin/events/${id}/registrations/${registrationId}`, {
      method: 'PATCH',
      body: JSON.stringify(input),
    });
    return response.data;
  }

  /** The attendance sheet: the days to tick and one line per person expected. */
  async getEventAttendance(id: number): Promise<ApiEventAttendanceSheet> {
    const response = await this.request<{ data: ApiEventAttendanceSheet }>(`/admin/events/${id}/attendance`);
    return response.data;
  }

  /**
   * Ticks (or clears) one cell: `registrationId` for a line the club picked,
   * `code` for the card just scanned, `date` plus `halfDay` — "day" for a whole
   * day, "am"/"pm" when the event is checked by half-day.
   */
  async markEventAttendance(
    id: number,
    input: { registrationId?: number; code?: string; date: string; halfDay?: "day" | "am" | "pm"; present?: boolean },
  ): Promise<ApiEventAttendanceRow> {
    const response = await this.request<{ data: ApiEventAttendanceRow }>(`/admin/events/${id}/attendance`, {
      method: 'POST',
      body: JSON.stringify(input),
    });
    return response.data;
  }

  /**
   * The award screen: the event's badge, the rule, and one line per person who
   * could receive it, already flagged with whether they meet the rule.
   */
  async getEventAwardBoard(id: number): Promise<ApiEventAwardBoard> {
    const response = await this.request<{ data: ApiEventAwardBoard }>(`/admin/events/${id}/award-preview`);
    return response.data;
  }

  /** Awards the event's badge to the ticked people. The server re-checks every id. */
  async awardEvent(
    id: number,
    input: { registrationIds: number[]; speakerIds: number[]; results?: Record<number, BadgeResultInput> },
  ): Promise<ApiEventAwardResult> {
    const { results, ...rest } = input;
    // Results travel as rows keyed by registration; without them the plain id list still works.
    const body = results
      ? { speakerIds: rest.speakerIds, results: rest.registrationIds.map((registrationId) => ({ registrationId, ...(results[registrationId] ?? { rank: null, division: null }) })) }
      : rest;
    const response = await this.request<{ data: ApiEventAwardResult }>(`/admin/events/${id}/award`, {
      method: 'POST',
      body: JSON.stringify(body),
    });
    return response.data;
  }

  /** Multipart write (badge image, badge create): same auth and errors as uploadClubLogo. */
  private async multipart<T>(endpoint: string, body: FormData): Promise<T> {
    const response = await this.authFetch(`${this.baseUrl}${endpoint}`, {
      method: 'POST',
      headers: { ...(this.token ? { Authorization: `Bearer ${this.token}` } : {}) },
      body,
    });
    if (response.status === 401) {
      this.handleUnauthorized();
      throw new Error('Unauthorized');
    }
    if (!response.ok) {
      const errorData = await response.json().catch(() => null);
      throw new Error(errorData?.error?.message || 'Failed to save');
    }
    return response.json();
  }

  // --- Club mini site (docs/club-sites-plan.md) ---------------------------

  /** The draft, its catalog of section types and its last snapshots. */
  async getClubSite(): Promise<ApiClubSite> {
    const response = await this.request<{ data: ApiClubSite }>('/admin/site');
    return response.data;
  }

  async updateClubSite(input: {
    slug?: string;
    theme?: { preset?: string; primaryColor?: string | null; accentColor?: string | null };
    keepInSync?: boolean;
  }): Promise<ApiClubSite> {
    const response = await this.request<{ data: ApiClubSite }>('/admin/site', {
      method: 'PUT',
      body: JSON.stringify(input),
    });
    return response.data;
  }

  /** What the club states about itself on its site (mentions légales). */
  async getSiteLegal(): Promise<ApiSiteLegal> {
    const response = await this.request<{ data: ApiSiteLegal }>('/admin/site/legal');
    return response.data;
  }

  async updateSiteLegal(input: Partial<Record<keyof ApiSiteLegal['legal'], string | null>>): Promise<ApiSiteLegal> {
    const response = await this.request<{ data: ApiSiteLegal }>('/admin/site/legal', {
      method: 'PUT',
      body: JSON.stringify(input),
    });
    return response.data;
  }

  /** Visitors' requests to take a photo down: open ones first, then the latest closed. */
  async getSitePhotoRemovals(): Promise<{ openCount: number; requests: ApiSitePhotoRemoval[] }> {
    const response = await this.request<{ data: { openCount: number; requests: ApiSitePhotoRemoval[] } }>('/admin/site/photo-removals');
    return response.data;
  }

  async closeSitePhotoRemoval(id: number, outcome: { done: true } | { reject: string }): Promise<ApiSitePhotoRemoval> {
    const response = await this.request<{ data: ApiSitePhotoRemoval }>(
      `/admin/site/photo-removals/${id}/${'done' in outcome ? 'done' : 'reject'}`,
      { method: 'POST', body: 'reject' in outcome ? JSON.stringify({ reason: outcome.reject }) : undefined },
    );
    return response.data;
  }

  /** Replaces the whole ordered list of sections. */
  async updateClubSiteSections(
    sections: { type: string; variant: string; enabled: boolean; settings: Record<string, unknown> }[],
  ): Promise<ApiClubSite> {
    const response = await this.request<{ data: ApiClubSite }>('/admin/site/sections', {
      method: 'PUT',
      body: JSON.stringify({ sections }),
    });
    return response.data;
  }

  /** Freezes the draft and renders it. `publishedNow` is false when nothing changed. */
  async publishClubSite(): Promise<{ site: ApiClubSite; publishedNow: boolean }> {
    const response = await this.request<{ data: ApiClubSite & { publishedNow: boolean } }>(
      '/admin/site/publish',
      { method: 'POST' },
    );
    const { publishedNow, ...site } = response.data;
    return { site, publishedNow };
  }

  /** What a publish would change on the live site, from the saved draft. */
  async getClubSiteChanges(): Promise<ApiSiteChange[]> {
    const response = await this.request<{ data: { changes: ApiSiteChange[] } }>('/admin/site/changes');
    return response.data.changes;
  }

  async rollbackClubSite(snapshotId: number): Promise<ApiClubSite> {
    const response = await this.request<{ data: ApiClubSite }>(`/admin/site/rollback/${snapshotId}`, {
      method: 'POST',
    });
    return response.data;
  }

  /** Asks the worker to declare the site's host again (failed certificate, new slug). */
  async retryClubSiteDomain(): Promise<ApiClubSite> {
    const response = await this.request<{ data: ApiClubSite }>('/admin/site/retry-domain', {
      method: 'POST',
    });
    return response.data;
  }

  /** Attaches the club's own domain and returns the two records to publish. */
  async attachClubSiteDomain(host: string): Promise<ApiClubSite> {
    const response = await this.request<{ data: ApiClubSite }>('/admin/site/domain', {
      method: 'PUT',
      body: JSON.stringify({ host }),
    });
    return response.data;
  }

  async removeClubSiteDomain(): Promise<ApiClubSite> {
    const response = await this.request<{ data: ApiClubSite }>('/admin/site/domain', { method: 'DELETE' });
    return response.data;
  }

  /** Reads the TXT and the address now instead of waiting for the 10-minute pass. */
  async checkClubSiteDomain(): Promise<ApiClubSite> {
    const response = await this.request<{ data: ApiClubSite }>('/admin/site/domain/check', {
      method: 'POST',
    });
    return response.data;
  }

  async getStripeStatus(): Promise<ApiStripeStatus> {
    const response = await this.request<{ data: ApiStripeStatus }>('/admin/payments/stripe');
    return response.data;
  }

  /** The Stripe-hosted onboarding URL; the caller sends the browser there. */
  async connectStripe(): Promise<{ url: string }> {
    const response = await this.request<{ data: { url: string } }>('/admin/payments/stripe/connect', { method: 'POST' });
    return response.data;
  }

  async getClubSiteMedia(): Promise<ApiSiteMediaList> {
    const response = await this.request<{ data: ApiSiteMediaList }>('/admin/site/media');
    return response.data;
  }

  /**
   * Uploads one photo. The server re-encodes it to WebP and answers the whole
   * list with the updated quota; multipart keeps the browser boundary. A picker
   * passes `attached`: the image serves one place and stays out of the gallery.
   */
  async uploadClubSiteMedia(
    file: File,
    alt?: string,
    imageRights?: boolean,
    target: 'gallery' | 'attached' = 'gallery',
    tags: string[] = [],
  ): Promise<ApiSiteMediaList & { added?: ApiSiteMediaItem }> {
    const form = new FormData();
    form.append('file', file);
    tags.forEach((tag) => form.append('tags[]', tag));
    if (alt) form.append('alt', alt);
    if (imageRights !== undefined) form.append('rights', imageRights ? '1' : '0');
    if (target === 'attached') form.append('target', 'attached');

    const response = await this.request<{ data: ApiSiteMediaList & { added?: ApiSiteMediaItem } }>(
      '/admin/site/media',
      {
        method: 'POST',
        body: form,
      },
    );
    return response.data;
  }

  async deleteClubSiteMedia(id: number): Promise<ApiSiteMediaList> {
    const response = await this.request<{ data: ApiSiteMediaList }>(`/admin/site/media/${id}`, {
      method: 'DELETE',
    });
    return response.data;
  }

  /** The caption and the image-rights confirmation of a photo already uploaded. */
  async updateClubSiteMedia(
    id: number,
    input: { imageRights?: boolean; alt?: string | null; tags?: string[] },
  ): Promise<ApiSiteMediaList> {
    const response = await this.request<{ data: ApiSiteMediaList }>(`/admin/site/media/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(input),
    });
    return response.data;
  }

  /** Adds and removes tags on several photos at once. */
  async tagClubSiteMedia(ids: number[], input: { add?: string[]; remove?: string[] }): Promise<ApiSiteMediaList> {
    const response = await this.request<{ data: ApiSiteMediaList }>('/admin/site/media/tags', {
      method: 'POST',
      body: JSON.stringify({ ids, ...input }),
    });
    return response.data;
  }

  // --- photo albums ------------------------------------------------------

  async getClubSiteGalleries(): Promise<ApiSiteGalleries> {
    const response = await this.request<{ data: ApiSiteGalleries }>('/admin/site/galleries');
    return response.data;
  }

  async createClubSiteGallery(input: ApiSiteGalleryInput): Promise<ApiSiteGalleries & { gallery: ApiSiteGallery }> {
    const response = await this.request<{ data: ApiSiteGalleries & { gallery: ApiSiteGallery } }>('/admin/site/galleries', {
      method: 'POST',
      body: JSON.stringify(input),
    });
    return response.data;
  }

  async updateClubSiteGallery(id: number, input: ApiSiteGalleryInput): Promise<ApiSiteGalleries & { gallery: ApiSiteGallery }> {
    const response = await this.request<{ data: ApiSiteGalleries & { gallery: ApiSiteGallery } }>(`/admin/site/galleries/${id}`, {
      method: 'PUT',
      body: JSON.stringify(input),
    });
    return response.data;
  }

  async deleteClubSiteGallery(id: number): Promise<ApiSiteGalleries> {
    const response = await this.request<{ data: ApiSiteGalleries }>(`/admin/site/galleries/${id}`, { method: 'DELETE' });
    return response.data;
  }

  // --- the club's news (phase 4) -----------------------------------------

  async getClubSiteArticles(): Promise<ApiSiteArticles> {
    const response = await this.request<{ data: ApiSiteArticles }>('/admin/site/articles');
    return response.data;
  }

  async createClubSiteArticle(
    input: ApiSiteArticleInput,
  ): Promise<ApiSiteArticles & { article: ApiSiteArticle }> {
    const response = await this.request<{ data: ApiSiteArticles & { article: ApiSiteArticle } }>(
      '/admin/site/articles',
      { method: 'POST', body: JSON.stringify(input) },
    );
    return response.data;
  }

  async updateClubSiteArticle(
    id: number,
    input: ApiSiteArticleInput,
  ): Promise<ApiSiteArticles & { article: ApiSiteArticle }> {
    const response = await this.request<{ data: ApiSiteArticles & { article: ApiSiteArticle } }>(
      `/admin/site/articles/${id}`,
      { method: 'PUT', body: JSON.stringify(input) },
    );
    return response.data;
  }

  async deleteClubSiteArticle(id: number): Promise<ApiSiteArticles> {
    const response = await this.request<{ data: ApiSiteArticles }>(`/admin/site/articles/${id}`, {
      method: 'DELETE',
    });
    return response.data;
  }

  // --- the club's partners (phase 6) --------------------------------------

  async getClubSitePartners(): Promise<ApiSitePartners> {
    const response = await this.request<{ data: ApiSitePartners }>('/admin/site/partners');
    return response.data;
  }

  /** Replaces the whole ordered list: a row left out is deleted. */
  async saveClubSitePartners(partners: ApiSitePartnerInput[]): Promise<ApiSitePartners> {
    const response = await this.request<{ data: ApiSitePartners }>('/admin/site/partners', {
      method: 'PUT',
      body: JSON.stringify({ partners }),
    });
    return response.data;
  }

  /** The draft rendered as HTML, for the editor's preview pane (auth header, so a plain iframe cannot load it). */
  async getClubSitePreviewHtml(theme?: { preset: string; primaryColor: string | null; accentColor: string | null }): Promise<string> {
    // An unsaved theme choice travels in the query: the page shows it at once.
    const query = theme
      ? `?${new URLSearchParams({ preset: theme.preset, primaryColor: theme.primaryColor ?? '', accentColor: theme.accentColor ?? '' }).toString()}`
      : '';
    const response = await this.authFetch(`${this.baseUrl}/admin/site/preview${query}`, {
      headers: this.token ? { Authorization: `Bearer ${this.token}` } : {},
    });
    if (response.status === 401) {
      this.handleUnauthorized();
      throw new Error('Unauthorized');
    }
    if (!response.ok) {
      // The server answers a failure as JSON: a reference the club can quote,
      // and the cause when the app runs in development.
      const body = (await response.json().catch(() => null)) as ApiError | null;
      const details = body?.error?.details;
      throw new ApiHttpError(
        body?.error?.message ?? i18n.t('admin:adminSite.previewError'),
        response.status,
        details !== null && typeof details === 'object' ? (details as Record<string, any>) : null,
      );
    }

    return response.text();
  }

  // --- the coach's card on the club site (phase 3) -------------------------

  /** The caller's own card in the active club. 403 when they do not coach there. */
  async getMySiteProfile(): Promise<ApiMySiteProfile> {
    const response = await this.request<{ data: ApiMySiteProfile }>('/me/site-profile');
    return response.data;
  }

  /** photoId: null keeps the photo, 0 removes it. */
  async saveMySiteProfile(input: {
    granted: boolean;
    displayName?: string | null;
    bio?: string | null;
    photoId?: number | null;
  }): Promise<ApiMySiteProfile> {
    const response = await this.request<{ data: ApiMySiteProfile }>('/me/site-profile', {
      method: 'PUT',
      body: JSON.stringify(input),
    });
    return response.data;
  }

  /** Uploads a card photo through the same WebP + quota pipeline as the gallery. */
  async uploadMySitePhoto(file: File): Promise<{ id: number | null; url: string | null; bytes: number }> {
    const form = new FormData();
    form.append('file', file);

    const response = await this.request<{ data: { id: number | null; url: string | null; bytes: number } }>(
      '/me/site-profile/photo',
      { method: 'POST', body: form },
    );
    return response.data;
  }

  /** Admin view: who opted in, who did not. Read-only, no grant button. */
  async getAdminSiteCoaches(): Promise<ApiAdminSiteCoaches> {
    const response = await this.request<{ data: ApiAdminSiteCoaches }>('/admin/site/coaches');
    return response.data;
  }

  /** Emails a sign-in link; the coach decides on their own account page. */
  async inviteSiteCoach(userId: number): Promise<void> {
    await this.request(`/admin/site/coaches/${userId}/invite`, { method: 'POST' });
  }
}

export const apiClient = new ApiClient();